import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate, WorkspaceLink } from "../hooks/useWsNavigate";
import { usePageTitle } from "../contexts/PageTitleContext";
import PhoneInput from "react-phone-input-2";
import { isValidPhoneNumber } from "libphonenumber-js";
import { getToken } from "../utils/auth";
import { pickVoiceForLanguage, voicesForLanguage } from "../utils/voiceLanguage";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import {
  aiTestingAgentAPI,
  voiceConfigAPI,
  twilioAPI,
  clientPhoneNumbersAPI,
  integrationAPI,
  systemAPI,
  clientIntegrationsAPI,
} from "../services/api";
import { useToast } from "../contexts/ToastContext";
import { usePostHog } from "@posthog/react";
import Tabs from "../components/ui/Tabs";
import PageHeader from "../components/layout/PageHeader";
import {
  LLM_PROVIDERS,
  TTS_PROVIDERS,
  ASR_PROVIDERS,
  LLM_API_KEY_HINTS,
  TTS_API_KEY_HINTS,
  ASR_API_KEY_HINTS,
  PROVIDER_DEFAULTS,
} from "../constants/providerCatalogue";
import FormField from "../components/ui/FormField";
import OpeningPromptField from "../components/agents/OpeningPromptField";
import FormAlert from "../components/ui/FormAlert";
import { Play, Pause, RefreshCw, ClipboardPaste } from "lucide-react";
import "react-phone-input-2/lib/style.css";

// ── Phone input CSS ────────────────────────────────────────────────────────────
const phoneInputStyles = `
  .react-tel-input { width: 100% !important; }
  .react-tel-input .form-control {
    width: 100% !important; height: 38px !important;
    border: 1px solid #D1D5DB !important; border-radius: 0.375rem !important;
    font-size: 14px !important; padding-left: 48px !important;
  }
  .react-tel-input .flag-dropdown {
    border: 1px solid #D1D5DB !important; border-right: none !important;
    border-radius: 0.375rem 0 0 0.375rem !important; background-color: white !important;
  }
  .react-tel-input .selected-flag {
    width: 38px !important; padding: 0 0 0 8px !important;
    border-radius: 0.375rem 0 0 0.375rem !important; background-color: white !important;
  }
  .react-tel-input .selected-flag .arrow { left: 22px !important; }
  .react-tel-input .flag-dropdown:hover,
  .react-tel-input .flag-dropdown.open,
  .react-tel-input .selected-flag:hover,
  .react-tel-input .selected-flag.open { background-color: white !important; }
  .react-tel-input .country-list {
    border: 1px solid #D1D5DB !important; border-radius: 0.375rem !important; margin-top: 2px !important;
  }
  .react-tel-input .form-control:focus {
    border-color: black !important; box-shadow: 0 0 0 1px black !important;
  }
  .react-tel-input .form-control:focus ~ .flag-dropdown {
    border-color: black !important; box-shadow: -1px 0 0 1px black !important;
  }
`;

const INPUT_CLS =
  "w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black";
const SELECT_CLS = INPUT_CLS;

const Field = FormField;

// Is this a real, dialable phone number? react-phone-input-2 gives us the
// national significant number prefixed with the country calling code but no
// "+", so we add it back before handing it to libphonenumber-js (the same
// library family the backend validates with, via phonelib). Catches a number
// that's too short for its country ("+91 32432 4232" — India needs 10 national
// digits) before it reaches the API and, worse, before it silently fails every
// scenario call in the first batch.
const isDialableNumber = (raw) => {
  if (!raw) return false;
  const e164 = String(raw).trim().startsWith("+") ? String(raw).trim() : `+${raw}`;
  try {
    return isValidPhoneNumber(e164);
  } catch {
    return false;
  }
};

// ── Inline voice preview button ────────────────────────────────────────────────
// Reuses a single Audio element at module scope so switching between voices
// (or cards) auto-stops the previously-playing clip — avoids the case where
// two samples overlap if the user spam-clicks.
let activeAudio = null;

const VoicePreviewButton = ({ url }) => {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    // Stop playback if the selected voice changes while audio is mid-play.
    return () => {
      if (activeAudio) {
        activeAudio.pause();
        activeAudio = null;
      }
      setPlaying(false);
    };
  }, [url]);

  if (!url) return null;

  const toggle = () => {
    if (playing && activeAudio) {
      activeAudio.pause();
      activeAudio = null;
      setPlaying(false);
      return;
    }
    if (activeAudio) {
      activeAudio.pause();
      activeAudio = null;
    }
    const audio = new Audio(url);
    audio.addEventListener("ended", () => setPlaying(false));
    audio.addEventListener("error", () => setPlaying(false));
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    activeAudio = audio;
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={playing ? "Stop preview" : "Play preview"}
      className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-input text-foreground hover:bg-muted/50 transition-colors"
    >
      {playing ? (
        <Pause className="h-4 w-4" />
      ) : (
        <Play className="h-4 w-4" />
      )}
    </button>
  );
};

// ── Generic config field renderer ──────────────────────────────────────────────
const ConfigField = ({ field, value, onChange, langCode, voiceCatalog, onLoadVoiceCatalog }) => {
  // Filter options by language map
  let options = field.options || [];
  if (field.language_dependent && field.language_map) {
    const allowed =
      field.language_map[langCode] || field.language_map["_default"] || null;
    if (allowed) options = options.filter((o) => allowed.includes(o.value));
  }

  // Kick off the voice catalog fetch the first time a select_from_api field
  // with a known source mounts. The parent memoizes per source, so this is
  // safe to run on every render.
  useEffect(() => {
    if (field.type === "select_from_api" && field.api_source && onLoadVoiceCatalog) {
      onLoadVoiceCatalog(field.api_source);
    }
  }, [field.type, field.api_source, onLoadVoiceCatalog]);

  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2.5 cursor-pointer mt-1">
        <div
          onClick={() => onChange(!value)}
          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
            value ? "bg-gray-900" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${
              value ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </div>
        {field.description && (
          <span className="text-xs text-muted-foreground">{field.description}</span>
        )}
      </label>
    );
  }

  if (field.type === "select" || field.type === "select_from_api") {
    if (field.type === "select_from_api") {
      const catalog = voiceCatalog || { status: "loading", voices: [] };
      const knownSource = field.api_source === "elevenlabs_voices" ||
                          field.api_source === "cartesia_voices";

      // Unknown api_source or hard error: fall back to a free-text input so
      // the user isn't blocked if the vendor API is unreachable.
      if (!knownSource || catalog.status === "error") {
        return (
          <div className="space-y-1">
            <input
              type="text"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              className={INPUT_CLS}
              placeholder={field.description || "Enter ID..."}
            />
            {catalog.status === "error" && (
              <p className="text-xs text-amber-600">
                Couldn't load voice list ({catalog.error}). Enter the ID manually.
              </p>
            )}
          </div>
        );
      }

      if (catalog.status === "loading") {
        return (
          <div className={`${SELECT_CLS} bg-muted text-muted-foreground flex items-center`}>
            Loading voices…
          </div>
        );
      }

      const allVoices = catalog.voices || [];
      // Filter the fetched vendor voices by the agent's primary language when the
      // voice objects carry a language tag (Cartesia/ElevenLabs). Fall back to the
      // full list if the filter would empty the dropdown (e.g. a language with no
      // native voice → keep the multilingual defaults visible).
      let voices = allVoices;
      if (langCode) {
        const filtered = voicesForLanguage(allVoices, langCode);
        if (filtered.length) voices = filtered;
      }
      const selectedVoice = allVoices.find((v) => v.id === value);
      return (
        <div className="flex items-center gap-2">
          <select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value || null)}
            className={SELECT_CLS}
          >
            <option value="">— Select voice —</option>
            {voices.map((v) => {
              const label = [v.name, v.language, v.gender]
                .filter(Boolean)
                .join(" · ");
              return (
                <option key={v.id} value={v.id}>
                  {label || v.id}
                </option>
              );
            })}
          </select>
          <VoicePreviewButton url={selectedVoice?.preview_url} />
        </div>
      );
    }
    return (
      <select
        value={value ?? field.default ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={opt.value ?? ""}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "float" || field.type === "int") {
    const current = value ?? field.default ?? field.min ?? 0;
    return (
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={field.min}
          max={field.max}
          step={field.step || 1}
          value={current}
          onChange={(e) =>
            onChange(
              field.type === "int"
                ? parseInt(e.target.value, 10)
                : parseFloat(e.target.value),
            )
          }
          className="flex-1 accent-gray-900"
        />
        <span className="text-xs font-mono w-12 text-right text-foreground">
          {current}
        </span>
      </div>
    );
  }

  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT_CLS}
      placeholder={field.description || ""}
    />
  );
};

// ── Provider card (LLM / TTS / ASR) ───────────────────────────────────────────
const ProviderCard = ({
  title,
  subtitle,
  providers,
  selectedProvider,
  config,
  langCode,
  onProviderChange,
  onConfigChange,
  apiKeyHint,
  apiKeyConfigured,
  voiceCatalogs,
  onLoadVoiceCatalog,
}) => {
  const providerDef = providers[selectedProvider] || {};
  const fields = providerDef.fields || [];
  // Only surface the "missing key" warning when the backend confirms the
  // key is actually absent. `undefined` (status still loading) is treated as
  // "assume configured" so we don't flash a false-positive warning.
  const showApiKeyWarning = apiKeyHint && apiKeyConfigured === false;

  return (
    <div className="border border-border rounded-xl p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>

      {/* Provider selector */}
      <Field label="Provider" required>
        <select
          value={selectedProvider}
          onChange={(e) => onProviderChange(e.target.value)}
          className={SELECT_CLS}
        >
          {Object.entries(providers).map(([key, def]) => (
            <option key={key} value={key}>
              {def.label || key}
            </option>
          ))}
        </select>
      </Field>

      {/* API key hint — only when the backend confirms the key is missing. */}
      {showApiKeyWarning && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
          <span className="font-medium shrink-0">API Key:</span>
          <span>
            Set <code className="font-mono">{apiKeyHint.envVar}</code> in the
            Python tools <code className="font-mono">.env</code> file.
          </span>
        </div>
      )}

      {/* Dynamic fields */}
      {fields.map((field) => (
        <Field
          key={field.key}
          label={field.label}
          hint={field.type !== "boolean" ? field.description : undefined}
          required={field.required}
        >
          <ConfigField
            field={field}
            value={config[field.key]}
            onChange={(val) => onConfigChange(field.key, val)}
            langCode={langCode}
            voiceCatalog={voiceCatalogs?.[field.api_source]}
            onLoadVoiceCatalog={onLoadVoiceCatalog}
          />
        </Field>
      ))}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
// Tabs with `internalOnly: true` only surface for internal admins (rubrichq
// staff by email). Everyone else never sees them in the nav.
const TABS = [
  { id: "instructions", label: "Configuration" },
  { id: "model", label: "Model Settings", internalOnly: true },
  { id: "channels", label: "Channels" },
];

const CreateAgent = () => {
  const [activeTab, setActiveTab] = useState("instructions");
  const navigate = useWsNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { showToast } = useToast();
  const { isAuthenticated, currentWorkspace, user } = useAuth();
  const { refreshAgents } = useAgent();
  const posthog = usePostHog();
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();

  const visibleTabs = user?.internal_admin ? TABS : TABS.filter((t) => !t.internalOnly);

  // If a non-internal user lands on a gated tab via deep link or stale state,
  // bounce them to the first visible tab.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "instructions");
    }
  }, [activeTab, visibleTabs]);

  // Provider API key status — fetched once. `null` while loading (warning
  // stays suppressed so we don't flash a false positive); keys map to
  // LLM/TTS/ASR_API_KEY_HINTS provider names.
  const [providerKeyStatus, setProviderKeyStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    systemAPI
      .getProviderApiKeyStatus()
      .then((s) => { if (!cancelled) setProviderKeyStatus(s); })
      .catch((e) => {
        // Fail open — don't block the UI on a transient failure.
        console.warn("provider_api_key_status fetch failed:", e);
        if (!cancelled) setProviderKeyStatus({});
      });
    return () => { cancelled = true; };
  }, []);
  const isKeyConfigured = (providerName) =>
    providerKeyStatus == null ? undefined : !!providerKeyStatus[providerName];

  // Voice-catalog cache, keyed by api_source ("elevenlabs_voices" /
  // "cartesia_voices"). Each entry is { status: "loading"|"ready"|"error",
  // voices: [...], error: string? }. Fetched lazily the first time a
  // ConfigField with that api_source mounts.
  const [voiceCatalogs, setVoiceCatalogs] = useState({});
  const loadVoiceCatalog = useCallback((apiSource) => {
    if (!apiSource) return;
    setVoiceCatalogs((prev) => {
      if (prev[apiSource]) return prev;
      return { ...prev, [apiSource]: { status: "loading", voices: [] } };
    });

    const fetcher =
      apiSource === "elevenlabs_voices"
        ? voiceConfigAPI.getElevenLabsVoices
        : apiSource === "cartesia_voices"
        ? voiceConfigAPI.getCartesiaVoices
        : null;
    if (!fetcher) return;

    fetcher()
      .then((resp) => {
        const voices = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
          ? resp
          : [];
        setVoiceCatalogs((prev) => ({
          ...prev,
          [apiSource]: { status: "ready", voices },
        }));
      })
      .catch((err) => {
        console.warn(`Voice catalog (${apiSource}) fetch failed:`, err);
        setVoiceCatalogs((prev) => ({
          ...prev,
          [apiSource]: {
            status: "error",
            voices: [],
            error: err?.message || "Failed to load voices",
          },
        }));
      });
  }, []);

  useEffect(() => {
    setPageTitle(isEditMode ? "Edit Agent" : "Create Agent");
    setParentPage({ name: "Agents", href: "/ai-testing-agent" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [isEditMode, setPageTitle, setParentPage, setShowBackButton]);

  // ── Instructions state ─────────────────────────────────────────────────────
  const [agentName, setAgentName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [conversationType, setConversationType] = useState("inbound");
  const [language, setLanguage] = useState("");
  const [languages, setLanguages] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [instructionType, setInstructionType] = useState("copy-paste");
  // Agent type drives instruction behavior (custom/pipecat/livekit = self-defined;
  // vapi/retell = vendor sync). Transport is chosen separately on the Channels tab.
  const [agentType, setAgentType] = useState("custom");

  // ── External provider integration state ───────────────────────────────────
  const [extVendor, setExtVendor] = useState("vapi");

  // Selecting an agent type keeps the legacy instructionType + extVendor in sync
  // so the existing conditionals (and the save payload) keep working.
  const selectAgentType = (v) => {
    setAgentType(v);
    if (v === "vapi" || v === "retell") {
      setInstructionType("external-provider");
      setExtVendor(v);
    } else {
      setInstructionType("copy-paste");
    }
  };
  // extApiKey removed — now managed in workspace Settings → Integrations
  const [extPublicKey, setExtPublicKey] = useState("");
  const [extAssistantId, setExtAssistantId] = useState("");
  const [extAutoSyncInstructions, setExtAutoSyncInstructions] = useState(false);
  // "sync" — pull instructions from the vendor; "manual" — user pastes them.
  // Mutually exclusive with instruction auto-sync (manual disables auto-sync).
  const [extInstructionSource, setExtInstructionSource] = useState("sync");
  const [extAutoFetchCalls, setExtAutoFetchCalls] = useState(false);
  const [extMinCallDuration, setExtMinCallDuration] = useState(10);
  const [extFetchInterval, setExtFetchInterval] = useState(1);
  const [extFetchUnit, setExtFetchUnit] = useState("hours"); // "minutes" | "hours"
  const [extSyncing, setExtSyncing] = useState(false);
  // Floor date for call sync — we only pull vendor calls created on/after this
  // date so we never import the agent's entire call history. Defaults to today.
  const [extFetchFromDate, setExtFetchFromDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [extFetchingCalls, setExtFetchingCalls] = useState(false);

  // Integration-connection state for the selected vendor. Three shapes:
  //   null         — not fetched yet
  //   { status: "disconnected" } — no row in client_integrations yet
  //   { status: "connected", id } — row exists and has credentials
  //   { status: "error", id }     — row exists but last test failed
  const [extIntegration, setExtIntegration] = useState(null);
  const [extIntegrationLoading, setExtIntegrationLoading] = useState(false);
  const [extConnectKey, setExtConnectKey] = useState("");
  const [extConnecting, setExtConnecting] = useState(false);

  // Fetch the current client_integrations list whenever the workspace or
  // selected vendor changes, and keep a reference to the matching row.
  useEffect(() => {
    if (!currentWorkspace || instructionType !== "external-provider") return;
    let cancelled = false;
    setExtIntegrationLoading(true);
    clientIntegrationsAPI
      .list(currentWorkspace.id)
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : list?.data || [];
        const match = rows.find((r) => r.provider === extVendor);
        setExtIntegration(match || { status: "disconnected", provider: extVendor });
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn("client_integrations list failed:", e);
          setExtIntegration({ status: "disconnected", provider: extVendor });
        }
      })
      .finally(() => {
        if (!cancelled) setExtIntegrationLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentWorkspace, extVendor, instructionType]);

  const connectVendorIntegration = async () => {
    if (!extConnectKey.trim()) {
      showToast("Enter an API key first", "error");
      return;
    }
    setExtConnecting(true);
    try {
      const vendorLabel = extVendor === "vapi" ? "Vapi" : "Retell";
      const payload = {
        provider: extVendor,
        credentials: { api_key: extConnectKey.trim() },
      };

      // If a row already exists (e.g. status=error from a prior failed
      // verify), update it — POST would 422 on the provider+client_id
      // uniqueness index and the user would be stuck re-entering the key.
      if (extIntegration?.id) {
        await clientIntegrationsAPI.update(currentWorkspace.id, extIntegration.id, payload);
      } else {
        await clientIntegrationsAPI.create(currentWorkspace.id, payload);
      }

      const list = await clientIntegrationsAPI.list(currentWorkspace.id);
      const rows = Array.isArray(list) ? list : list?.data || [];
      const match = rows.find((r) => r.provider === extVendor);
      setExtIntegration(match || { status: "disconnected", provider: extVendor });
      setExtConnectKey("");

      if (match?.status === "connected") {
        showToast(`${vendorLabel} connected`, "success");
      } else {
        showToast(
          `${vendorLabel} key saved but verification failed — check the key is valid.`,
          "error"
        );
      }
    } catch (e) {
      showToast(
        e.response?.data?.error ||
          e.response?.data?.errors?.join?.(", ") ||
          "Could not connect — check the API key.",
        "error"
      );
    } finally {
      setExtConnecting(false);
    }
  };

  const [firstMessageType, setFirstMessageType] = useState(
    "assistant-speaks-first",
  );
  const [firstMessageText, setFirstMessageText] = useState("");
  // What the provider's own config implied on the last sync. Kept apart from
  // firstMessageType, which the tester is free to change afterwards.
  const [syncedFirstMessageType, setSyncedFirstMessageType] = useState("");

  // ── Model settings state ───────────────────────────────────────────────────
  const [llmProvider, setLlmProvider] = useState(
    PROVIDER_DEFAULTS.llm_provider,
  );
  const [llmConfig, setLlmConfig] = useState({
    ...PROVIDER_DEFAULTS.llm_config,
    model: PROVIDER_DEFAULTS.llm_model,
  });
  const [ttsProvider, setTtsProvider] = useState(
    PROVIDER_DEFAULTS.tts_provider,
  );
  const [ttsConfig, setTtsConfig] = useState({
    ...PROVIDER_DEFAULTS.tts_config,
  });
  // Edit mode only: has the fetched agent populated the form yet? Until it
  // has, ttsConfig still holds PROVIDER_DEFAULTS and says nothing about this
  // agent, so the voice-seeding effect below must not read it.
  const [agentHydrated, setAgentHydrated] = useState(false);
  const [asrProvider, setAsrProvider] = useState(
    PROVIDER_DEFAULTS.asr_provider,
  );
  const [asrConfig, setAsrConfig] = useState({
    ...PROVIDER_DEFAULTS.asr_config,
  });
  const [maxCallDuration, setMaxCallDuration] = useState(null); // null = use global default (10 min)
  // Per-agent turn-taking override. null = inherit the workspace/admin default;
  // an object overrides it for this agent only (merged over admin in the backend).
  const [turnTaking, setTurnTaking] = useState(null);

  // ── Channels (Phone / Web / Text) ──────────────────────────────────────────
  // agent_kind is derived from instructions_type + extVendor (declared above).
  // No separate UI — the Configuration tab already picks the source.
  //
  // Phone channel is always on (every agent gets a phone channel by default);
  // web and text are independently enable-able. At scenario run time the
  // user picks which configured channel to exercise.
  const agentKind = instructionType === "external-provider"
    ? `external_${extVendor}`
    : "internal";
  const phoneEnabled = true; // phone is mandatory for every agent
  // Telephony carrier for the phone channel — twilio | plivo | vobiz | telnyx |
  // exotel (sip reserved, not selectable yet).
  const [phoneProvider, setPhoneProvider] = useState("twilio");
  // Did the operator actually pick a carrier, or is the radio just showing a
  // default? Only a click sets this — neither hydrating an existing agent nor
  // inheriting the workspace default counts, because both ARE defaults.
  //
  // It decides whether we send `provider` at all on create (see the phone
  // channel payload); the backend reads an absent provider as permission to
  // pick one from the agent's country.
  const [phoneProviderExplicit, setPhoneProviderExplicit] = useState(false);
  // New agents inherit the workspace's default carrier (admin-configurable);
  // edit mode hydrates the provider from the saved agent instead. Runs once when
  // the workspace's default_telephony_provider becomes available.
  useEffect(() => {
    if (!isEditMode && currentWorkspace?.default_telephony_provider) {
      setPhoneProvider(currentWorkspace.default_telephony_provider);
    }
  }, [isEditMode, currentWorkspace?.default_telephony_provider]);
  const [webEnabled, setWebEnabled] = useState(false);
  const [textEnabled, setTextEnabled] = useState(false);

  // Web provider — valid values depend on agent_kind.
  // internal:        livekit | daily | websocket
  // external_*:      vendor_websocket  (forced in the submit payload)
  const [webProvider, setWebProvider] = useState("livekit");

  // Legacy transportType — retained for payload compatibility during the
  // dual-write window. Derived from (agentKind, enabled channels, webProvider)
  // in handleSubmit rather than driven directly by UI anymore.
  const [transportType, setTransportType] = useState("pipecat_twilio");

  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState("");
  const [twilioPhoneNumberSid, setTwilioPhoneNumberSid] = useState("");
  const [twilioLoading, setTwilioLoading] = useState(false);
  // Workspace-scoped caller ID picker (replaces the legacy global Twilio list).
  // callerPhoneNumberId === "" means "use workspace default" (null on save).
  const [callerPhoneNumberId, setCallerPhoneNumberId] = useState("");
  const [workspacePhones, setWorkspacePhones] = useState({ default: null, assigned: [], is_internal_admin: false });
  const [webCallUrl, setWebCallUrl] = useState("");
  const [roomManagement, setRoomManagement] = useState("client");
  const [botRunnerUrl, setBotRunnerUrl] = useState("");
  const [livekitAgentName, setLivekitAgentName] = useState("");
  // Per-agent transport credential overrides + JSON config (web_channel.config).
  // Blank = fall back to the workspace credentials from Settings → Integrations.
  const [livekitUrl, setLivekitUrl] = useState("");
  const [livekitApiKey, setLivekitApiKey] = useState("");
  const [livekitApiSecret, setLivekitApiSecret] = useState("");
  const [livekitConfigJson, setLivekitConfigJson] = useState("");
  const [dailyApiKey, setDailyApiKey] = useState("");
  const [roomPropertiesJson, setRoomPropertiesJson] = useState("");
  // The API redacts the secrets; these flags say whether one is already stored
  // so we can show "configured" and avoid wiping it on save when left blank.
  const [livekitSecretSet, setLivekitSecretSet] = useState(false);
  const [dailyKeySet, setDailyKeySet] = useState(false);
  // When a secret is already stored we show a "saved" row; "Edit" reveals the
  // input so a new value can replace it.
  const [livekitSecretEditing, setLivekitSecretEditing] = useState(false);
  const [dailyKeyEditing, setDailyKeyEditing] = useState(false);

  // ── Text channel config ────────────────────────────────────────────────────
  // Text testing standardises on the Custom WebSocket (X-RUBRIC) protocol — the
  // single supported text format. Legacy REST/pipecat configs are coerced to it.
  const [textEndpointType, setTextEndpointType] = useState("custom_websocket");
  const [textEndpoint, setTextEndpoint] = useState("");
  const [textApiKey, setTextApiKey] = useState("");
  const [textApiFormat, setTextApiFormat] = useState("openai_chat");
  const [textWebsocketUrl, setTextWebsocketUrl] = useState("");
  // Custom WebSocket (X-RUBRIC) text interface
  const [textWsSecret, setTextWsSecret] = useState("");
  const [textWsSecretSet, setTextWsSecretSet] = useState(false); // true if backend reports a secret already saved
  const [textPerTurnTimeout, setTextPerTurnTimeout] = useState(120);
  const [textMaxSessionDuration, setTextMaxSessionDuration] = useState(600);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Load languages ─────────────────────────────────────────────────────────
  useEffect(() => {
    voiceConfigAPI
      .getAllLanguages()
      .then((r) => {
        const langs = r.data || [];
        setLanguages(langs);
        // Default to English (US) for new agents
        if (!isEditMode && !language) {
          const enUs = langs.find((l) => l.language_key?.toLowerCase() === "en-us" || l.language_key?.toLowerCase() === "en");
          if (enUs) setLanguage(enUs.language_key);
        }
      })
      .catch(() => {});
  }, []);

  // Renders the Outbound Caller ID picker. Admins get a dropdown over the
  // workspace's assigned numbers; non-admins see the workspace default as a
  // read-only label ("like how you can't edit instructions from here").
  const renderCallerIdPicker = () => {
    if (twilioLoading) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
          Loading workspace numbers...
        </div>
      );
    }

    const { default: defaultPn, assigned, is_internal_admin, managed_defaults } = workspacePhones;

    // The outbound caller ID (the actual phone number) is internal-only — never
    // surface it to external (non-admin) users. They can't change it anyway, and
    // exposing the number leaks routing infrastructure. Hide the field entirely.
    if (!is_internal_admin) {
      return null;
    }

    // Only numbers belonging to the selected carrier can originate this agent's
    // calls (a Plivo agent cannot dial from a Twilio number).
    const carrierNumbers = assigned.filter((n) => n.provider === phoneProvider);
    // Managed/shared default for this carrier (used when the workspace has none).
    const managedDefault = (managed_defaults || []).find((n) => n.provider === phoneProvider);
    const carrierDefault = defaultPn && defaultPn.provider === phoneProvider ? defaultPn : null;
    const defaultLabel = carrierDefault
      ? `${carrierDefault.friendly_name ? carrierDefault.friendly_name + " — " : ""}${carrierDefault.e164_number}`
      : null;
    const overrideOptions = carrierNumbers.filter((n) => n.id !== carrierDefault?.id);

    return (
      <Field label="Outbound Caller ID" hint="Defaults to workspace number. Admins may override per agent.">
        {carrierNumbers.length === 0 ? (
          managedDefault ? (
            <div className="text-xs text-muted-foreground py-2">
              Uses the managed {phoneProvider} number ({managedDefault.e164_number}).
            </div>
          ) : (
            <div className="text-xs text-muted-foreground py-2">
              No {phoneProvider} numbers in this workspace. Add one in Admin → Phone Numbers.
            </div>
          )
        ) : (
          <select
            value={callerPhoneNumberId}
            onChange={(e) => setCallerPhoneNumberId(e.target.value)}
            className={SELECT_CLS}
          >
            <option value="">
              {defaultLabel ? `Use workspace default (${defaultLabel})` : "Use workspace default (none configured)"}
            </option>
            {overrideOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.friendly_name ? `${n.friendly_name} — ` : ""}{n.e164_number}
              </option>
            ))}
          </select>
        )}
      </Field>
    );
  };

  // ── Load workspace phone numbers on channels-tab open ─────────────────────
  // This replaces the legacy global Twilio-inventory call with a workspace-
  // scoped list. Admins get a dropdown; non-admins see the default read-only.
  const fetchWorkspacePhones = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setTwilioLoading(true);
    try {
      const data = await clientPhoneNumbersAPI.list(currentWorkspace.id);
      setWorkspacePhones(data || { default: null, assigned: [], is_internal_admin: false });
    } catch {
      setWorkspacePhones({ default: null, assigned: [], is_internal_admin: false });
    } finally {
      setTwilioLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (activeTab === "channels") fetchWorkspacePhones();
  }, [activeTab, fetchWorkspacePhones]);

  // ── Load agent in edit mode ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !currentWorkspace || languages.length === 0) return;
    // Hydrate ONCE. currentWorkspace/languages are unstable references, so
    // without this guard the effect re-runs on unrelated re-renders and
    // re-hydrates the whole form from the server — silently clobbering
    // in-progress edits (e.g. resetting the phone carrier back to "twilio"
    // when you change the TTS model, then persisting twilio on save).
    if (agentHydrated) return;
    setLoading(true);
    aiTestingAgentAPI
      .getAgent(id, currentWorkspace.id)
      .then((agent) => {
        if (!agent) return;
        setAgentName(agent.name || "");
        setPhoneNumber(agent.phone_number?.replace(/^\+/, "") || "");
        setConversationType(agent.conversation_type || "inbound");
        setInstructions(agent.instructions || "");
        setInstructionType(agent.instructions_type || "copy-paste");
        setAgentType(
          agent.agent_type ||
            (agent.instructions_type === "external-provider"
              ? agent.external_vendor || "vapi"
              : "custom"),
        );
        const lang = languages.find((l) => l.id === agent.language_id);
        setLanguage(lang?.language_key || "");
        setFirstMessageType(
          agent.first_message?.type || "assistant-speaks-first",
        );
        // A saved external agent's stored type is whatever the last sync wrote,
        // so it doubles as the synced baseline. Without this the silent-call
        // warning would only ever fire in the same session as a Sync click.
        if (agent.first_message?.type) {
          setSyncedFirstMessageType(agent.first_message.type);
        }
        setFirstMessageText(agent.first_message?.message || "");

        if (agent.llm_provider) setLlmProvider(agent.llm_provider);
        if (agent.llm_config || agent.llm_model) {
          setLlmConfig({
            ...(agent.llm_config || {}),
            model: agent.llm_model || PROVIDER_DEFAULTS.llm_model,
          });
        }
        if (agent.tts_provider) setTtsProvider(agent.tts_provider);
        // Unconditional: the form is seeded with PROVIDER_DEFAULTS, whose
        // voice_id is the English "Skyler". Leaving that in place when the
        // agent stores no TTS config would present a hardcoded English voice
        // as if it were this agent's own, and saving would persist it.
        setTtsConfig(agent.tts_config || {});
        if (agent.asr_provider) setAsrProvider(agent.asr_provider);
        if (agent.asr_config) setAsrConfig(agent.asr_config);
        setTurnTaking(agent.turn_taking || null);
        if (agent.max_call_duration)
          setMaxCallDuration(agent.max_call_duration / 60); // store as minutes in UI

        if (agent.twilio_phone_number)
          setTwilioPhoneNumber(agent.twilio_phone_number);
        if (agent.twilio_phone_number_sid)
          setTwilioPhoneNumberSid(agent.twilio_phone_number_sid);
        if (agent.caller_phone_number_id)
          setCallerPhoneNumberId(String(agent.caller_phone_number_id));
        if (agent.web_call_url) setWebCallUrl(agent.web_call_url);
        if (agent.integration_type) setTransportType(agent.integration_type);
        if (agent.integration_config) {
          const ic = agent.integration_config;
          if (ic.room_management) setRoomManagement(ic.room_management);
          if (ic.bot_runner_url) setBotRunnerUrl(ic.bot_runner_url);
          if (ic.livekit_agent_name) setLivekitAgentName(ic.livekit_agent_name);
        }

        // ── Channel state hydration ───────────────────────────────────────
        // agent_kind is derived from instructions_type + extVendor (we don't
        // store it from the server response). Prefer the new channel jsonb;
        // fall back to legacy columns so agents saved under the old model
        // still show up correctly until they're re-saved.
        const legacyIsExternal = agent.integration_type?.startsWith("external_");
        const derivedKind =
          agent.agent_kind ||
          (agent.integration_type === "external_vapi" ? "external_vapi" :
           agent.integration_type === "external_retell" ? "external_retell" : "internal");

        const phoneCh = agent.phone_channel;
        const webCh   = agent.web_channel;
        const textCh  = agent.text_channel;

        // Phone is always on; just hydrate the carrier + Twilio config (if present).
        setPhoneProvider(phoneCh?.provider || "twilio");
        if (phoneCh?.config) {
          const pc = phoneCh.config;
          if (pc.twilio_phone_number) setTwilioPhoneNumber(pc.twilio_phone_number);
          if (pc.twilio_phone_number_sid) setTwilioPhoneNumberSid(pc.twilio_phone_number_sid);
        }

        if (webCh && webCh.provider) {
          setWebEnabled(true);
          setWebProvider(webCh.provider);
          const wc = webCh.config || {};
          if (wc.bot_runner_url) setBotRunnerUrl(wc.bot_runner_url);
          if (wc.room_management) setRoomManagement(wc.room_management);
          if (wc.livekit_agent_name) setLivekitAgentName(wc.livekit_agent_name);
          if (wc.web_call_url) setWebCallUrl(wc.web_call_url);
          const creds = wc.credentials || {};
          if (creds.livekit_url) setLivekitUrl(creds.livekit_url);
          if (creds.livekit_api_key) setLivekitApiKey(creds.livekit_api_key);
          if (creds.livekit_api_secret) setLivekitApiSecret(creds.livekit_api_secret);
          if (creds.daily_api_key) setDailyApiKey(creds.daily_api_key);
          // Secrets are redacted by the API — track whether one already exists.
          setLivekitSecretSet(!!creds.livekit_api_secret_set);
          setDailyKeySet(!!creds.daily_api_key_set);
          if (wc.livekit_config)
            setLivekitConfigJson(
              typeof wc.livekit_config === "string"
                ? wc.livekit_config
                : JSON.stringify(wc.livekit_config, null, 2),
            );
          if (wc.room_properties)
            setRoomPropertiesJson(
              typeof wc.room_properties === "string"
                ? wc.room_properties
                : JSON.stringify(wc.room_properties, null, 2),
            );
        } else {
          // Derive from legacy integration_type
          const legacyWebMap = {
            livekit_managed: "livekit",
            pipecat_livekit: "livekit",
            daily_managed: "daily",
            websocket: "websocket",
            external_vapi: "vendor_websocket",
            external_retell: "vendor_websocket",
          };
          const derivedWeb = legacyWebMap[agent.integration_type];
          if (derivedWeb) {
            setWebEnabled(true);
            setWebProvider(derivedWeb);
          }
        }

        if (textCh && textCh.provider) {
          setTextEnabled(true);
          const tc = textCh.config || {};
          // WebSocket is the only supported text format now — coerce any
          // legacy REST/pipecat_websocket config onto it.
          setTextEndpointType("custom_websocket");
          if (tc.text_endpoint) setTextEndpoint(tc.text_endpoint);
          if (tc.text_api_key) setTextApiKey(tc.text_api_key);
          if (tc.text_api_format) setTextApiFormat(tc.text_api_format);
          if (tc.text_websocket_url) setTextWebsocketUrl(tc.text_websocket_url);
          setTextWsSecretSet(!!tc.text_ws_secret_set);
          setTextPerTurnTimeout(tc.per_turn_timeout_s ?? 120);
          setTextMaxSessionDuration(tc.max_session_duration_s ?? 600);
        } else if (agent.text_config) {
          const tc = agent.text_config;
          // WebSocket is the only supported text format now — coerce any
          // legacy REST/pipecat_websocket config onto it.
          setTextEndpointType("custom_websocket");
          if (tc.text_endpoint) setTextEndpoint(tc.text_endpoint);
          if (tc.text_api_key) setTextApiKey(tc.text_api_key);
          if (tc.text_api_format) setTextApiFormat(tc.text_api_format);
          if (tc.text_websocket_url) setTextWebsocketUrl(tc.text_websocket_url);
          setTextWsSecretSet(!!tc.text_ws_secret_set);
          setTextPerTurnTimeout(tc.per_turn_timeout_s ?? 120);
          setTextMaxSessionDuration(tc.max_session_duration_s ?? 600);
          // Treat text channel as configured if endpoint or ws_url is set
          if (tc.text_endpoint || tc.text_websocket_url) setTextEnabled(true);
        }

        // If we detected external via agent_kind or legacy type, force web
        // provider to vendor_websocket (can't be livekit/daily for vendors).
        if (derivedKind !== "internal") {
          setWebProvider("vendor_websocket");
        }

        // Load integration data for external-provider agents
        if (
          agent.integration ||
          agent.integration_type?.startsWith("external_")
        ) {
          const vendor =
            agent.integration?.vendor ||
            agent.integration_type?.replace("external_", "") ||
            "vapi";
          setExtVendor(vendor);
          setExtAssistantId(
            agent.integration?.assistant_id ||
              agent.integration_config?.assistant_id ||
              "",
          );
          const instrSource =
            agent.integration_config?.instruction_source || "sync";
          setExtInstructionSource(instrSource);
          setExtAutoSyncInstructions(
            instrSource === "manual"
              ? false
              : agent.integration?.auto_sync_instructions ||
                  agent.integration_config?.auto_sync_instructions ||
                  false,
          );
          setExtAutoFetchCalls(
            agent.integration?.auto_fetch_calls ||
              agent.integration_config?.auto_fetch_calls ||
              false,
          );
          setExtMinCallDuration(
            agent.integration?.min_call_duration_seconds ??
              agent.integration_config?.min_call_duration_seconds ??
              10,
          );
          const mins =
            agent.integration?.fetch_calls_every_minutes ??
            agent.integration_config?.fetch_calls_every_minutes ??
            60;
          if (mins % 60 === 0 && mins >= 60) {
            setExtFetchUnit("hours");
            setExtFetchInterval(mins / 60);
          } else {
            setExtFetchUnit("minutes");
            setExtFetchInterval(mins);
          }
          const savedFrom =
            agent.integration?.fetch_calls_from ||
            agent.integration_config?.fetch_calls_from;
          if (savedFrom) setExtFetchFromDate(savedFrom.slice(0, 10));
        }
      })
      .catch(() => setError("Failed to load agent details"))
      .finally(() => {
        setLoading(false);
        setAgentHydrated(true);
      });
  }, [id, isEditMode, currentWorkspace, languages, agentHydrated]);

  // ── Provider change helpers ────────────────────────────────────────────────
  const handleLlmProviderChange = (p) => {
    setLlmProvider(p);
    const def = LLM_PROVIDERS[p];
    setLlmConfig({
      model: def?.default_model || "",
      temperature: 0.7,
      max_tokens: 150,
    });
  };

  const handleTtsProviderChange = (p) => {
    setTtsProvider(p);
    const defaults = {};
    (TTS_PROVIDERS[p]?.fields || []).forEach((f) => {
      if (f.default !== undefined) defaults[f.key] = f.default;
    });
    setTtsConfig(defaults);
  };

  const handleAsrProviderChange = (p) => {
    setAsrProvider(p);
    const defaults = {};
    (ASR_PROVIDERS[p]?.fields || []).forEach((f) => {
      if (f.default !== undefined) defaults[f.key] = f.default;
    });
    setAsrConfig(defaults);
  };

  const handleTwilioSelect = (e) => {
    const num = twilioNumbers.find((n) => n.phone_number === e.target.value);
    setTwilioPhoneNumber(num?.phone_number || "");
    setTwilioPhoneNumberSid(num?.sid || "");
  };

  // ── Language code (first segment, lowercased) for filtering ────────────────
  // Must be lowercase to match language_map keys (e.g. "en") and the lowercase
  // `language` tags on fetched vendor voices.
  const langCode = (language?.split(/[-_]/)[0] || "en").toLowerCase();

  // ── Seed the voice from the agent's language ───────────────────────────────
  // The dropdown already *filters* options by language, but nothing picked the
  // selected value — so a new agent set to Italian kept the hardcoded English
  // default voice_id and the test caller bot spoke English at it.
  //
  // This only seeds. On an EXISTING agent the stored voice is the operator's,
  // so we fill a blank one and otherwise leave it be: this effect also runs on
  // page load, so re-deriving it meant merely opening an agent and saving it
  // silently swapped a deliberately chosen voice for the language default.
  const ttsVoiceField = useMemo(
    () =>
      (TTS_PROVIDERS[ttsProvider]?.fields || []).find(
        (f) => f.type === "select_from_api" && f.api_source
      ) || null,
    [ttsProvider]
  );

  // The voice catalog is normally fetched when the voice ConfigField mounts,
  // but that field lives on the internal-admin-only "Model Settings" tab, so
  // load it here too — otherwise the seeding below has nothing to pick from.
  //
  // This only decides what an INTERNAL ADMIN sees and saves: AgentsController
  // strips tts_provider/tts_config from a normal user's request entirely. What
  // gives a normal user's agent a language-correct voice is the backend's
  // Agents::DefaultVoiceResolver, run out-of-band by AgentDefaultVoiceJob.
  useEffect(() => {
    if (ttsVoiceField?.api_source) loadVoiceCatalog(ttsVoiceField.api_source);
  }, [ttsVoiceField, loadVoiceCatalog]);

  useEffect(() => {
    if (!ttsVoiceField || !langCode) return;
    // Reading ttsConfig before the agent lands would compare against
    // PROVIDER_DEFAULTS and conclude this agent already has a voice.
    if (isEditMode && !agentHydrated) return;
    const catalog = voiceCatalogs[ttsVoiceField.api_source];
    if (catalog?.status !== "ready") return;

    setTtsConfig((prev) => {
      const current = prev?.[ttsVoiceField.key] ?? null;
      const next = pickVoiceForLanguage(catalog.voices, langCode, current, {
        replaceExisting: !isEditMode,
      });
      if (next === current) return prev;
      return { ...prev, [ttsVoiceField.key]: next };
    });
    // agentHydrated is a dependency so the blank-fill still runs for an agent
    // whose language matches the pre-hydration default and therefore never
    // changes langCode.
  }, [langCode, ttsVoiceField, voiceCatalogs, isEditMode, agentHydrated]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentWorkspace) {
      showToast("No workspace selected", "error");
      return;
    }
    if (!isAuthenticated || !getToken()) {
      showToast("Please log in", "error");
      return;
    }

    const isExternal = instructionType === "external-provider";

    // Resolve the selected language for BOTH agent kinds. Self-managed agents
    // must have one; external (vapi/retell) agents may sync it — and we still
    // persist it via language_id so the synced language actually sticks.
    const selectedLanguage = languages.find(
      (l) => l.language_key === language,
    );
    if (!isExternal) {
      if (!selectedLanguage) {
        setError("Please select a valid language");
        return;
      }
      if (!instructions.trim()) {
        setError("Agent instructions are required");
        return;
      }
    }

    // A present phone number must be dialable, for either agent kind. Presence
    // alone let malformed numbers through, and the scenario batch then failed
    // every call with no clear reason.
    if (phoneNumber && !isDialableNumber(phoneNumber)) {
      setError(
        "That agent phone number doesn't look valid — enter it in full " +
        "international format including the country code.",
      );
      return;
    }
    const languageId = selectedLanguage?.id || null;

    if (isExternal && extIntegration?.status !== "connected") {
      const vendorLabel = extVendor === "vapi" ? "Vapi" : "Retell";
      setError(`Connect ${vendorLabel} to this workspace before saving an external-provider agent.`);
      return;
    }

    if (isExternal && !extAssistantId) {
      setError("Assistant/Agent ID is required for external providers");
      return;
    }

    setLoading(true);
    setError(null);

    const formattedPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+${phoneNumber}`;
    const { model: llmModel, ...llmRest } = llmConfig;

    // ── Build channel payloads ─────────────────────────────────────────────
    // The backend accepts phone_channel / web_channel / text_channel jsonb
    // blobs and dual-writes to the legacy columns. Send `null` for disabled
    // channels so the backend clears them.
    const effectiveKind = isExternal ? `external_${extVendor}` : agentKind;
    const effectiveWebProvider = effectiveKind === "internal" ? webProvider : "vendor_websocket";

    const phoneChannelPayload = phoneEnabled
      ? {
          // Left out on create until the operator picks one: an absent provider
          // is what tells the backend it may default from the agent's country
          // (an Indian number has to be dialed over an Indian carrier, so +91
          // becomes Vobiz). Naming one here means it, and is never overridden.
          // In edit mode we always round-trip the stored value — the backend
          // treats an unchanged provider as "not a choice", so re-seeding on a
          // country change still works.
          ...((isEditMode || phoneProviderExplicit) && { provider: phoneProvider }),
          config: {
            twilio_phone_number: twilioPhoneNumber || null,
            twilio_phone_number_sid: twilioPhoneNumberSid || null,
          },
        }
      : null;

    const webChannelPayload = webEnabled
      ? {
          provider: effectiveWebProvider,
          config: {
            ...(["livekit", "daily"].includes(effectiveWebProvider) && {
              room_management: roomManagement,
              bot_runner_url: botRunnerUrl || null,
              livekit_agent_name: effectiveWebProvider === "livekit" ? (livekitAgentName || null) : null,
            }),
            ...(effectiveWebProvider === "livekit" && {
              credentials: {
                livekit_url: livekitUrl || null,
                livekit_api_key: livekitApiKey || null,
                livekit_api_secret: livekitApiSecret || null,
              },
              livekit_config: livekitConfigJson || null,
            }),
            ...(effectiveWebProvider === "daily" && {
              credentials: {
                daily_api_key: dailyApiKey || null,
              },
              room_properties: roomPropertiesJson || null,
            }),
            ...(effectiveWebProvider === "websocket" && {
              web_call_url: webCallUrl || null,
            }),
          },
        }
      : null;

    let textChannelPayload = null;
    if (textEnabled && effectiveKind === "internal") {
      const textConfig = {
        text_endpoint_type: textEndpointType,
        text_endpoint: textEndpoint || null,
        text_api_key: textApiKey || null,
        text_api_format: textApiFormat,
        text_websocket_url: textWebsocketUrl || null,
        text_ws_secret: textWsSecret || null,
        per_turn_timeout_s: textPerTurnTimeout,
        max_session_duration_s: textMaxSessionDuration,
      };
      // On edit: if the user left the secret blank but one is already saved,
      // omit the key entirely so we don't clobber the stored secret with null.
      if (!textWsSecret && textWsSecretSet) {
        delete textConfig.text_ws_secret;
      }
      textChannelPayload = {
        provider: textEndpointType === "rest" ? "rest" : "websocket",
        config: textConfig,
      };
    }

    // Derive a legacy integration_type from the enabled channels so
    // pre-migration code paths keep routing to a sensible connector.
    let derivedIntegrationType = "pipecat_twilio";
    if (isExternal) {
      derivedIntegrationType = `external_${extVendor}`;
    } else if (webEnabled) {
      const legacyMap = {
        livekit: "livekit_managed",
        daily: "daily_managed",
        websocket: "websocket",
      };
      derivedIntegrationType = legacyMap[effectiveWebProvider] || "pipecat_twilio";
    } else if (phoneEnabled) {
      derivedIntegrationType = "pipecat_twilio";
    }

    let formData;

    if (isExternal) {
      // External provider agents need name, type, integration credentials,
      // AND model settings (LLM/TTS/ASR control the test caller bot).
      formData = {
        name: agentName,
        conversation_type: conversationType,
        instructions_type: instructionType,
        instructions,
        first_message: {
          type: firstMessageType,
          message:
            firstMessageType === "assistant-speaks-first"
              ? firstMessageText
              : "",
        },
        // Our test bot dials this to reach the agent under test, exactly as it
        // does for a self-managed agent — and it selects the telephony carrier
        // server-side. Omitting it used to silently drop whatever was typed in
        // the (always-rendered) phone field.
        phone_number: formattedPhone,
        integration_type: `external_${extVendor}`,
        external_vendor: extVendor,
        agent_type: agentType,
        language_id: languageId,
        integration_config: {
          assistant_id: extAssistantId,
          instruction_source: extInstructionSource,
          auto_sync_instructions:
            extInstructionSource === "manual"
              ? false
              : extAutoSyncInstructions,
          auto_fetch_calls: extAutoFetchCalls,
          min_call_duration_seconds: Number(extMinCallDuration) || 10,
          fetch_calls_every_minutes:
            (Number(extFetchInterval) || 1) * (extFetchUnit === "hours" ? 60 : 1),
          fetch_calls_from: extFetchFromDate || null,
        },
        agent_kind: `external_${extVendor}`,
        phone_channel: phoneChannelPayload,
        web_channel: webChannelPayload,
        text_channel: null,
        llm_provider: llmProvider,
        llm_model: llmModel || PROVIDER_DEFAULTS.llm_model,
        llm_config: llmRest,
        tts_provider: ttsProvider,
        tts_config: ttsConfig,
        asr_provider: asrProvider,
        asr_config: asrConfig,
        turn_taking: turnTaking,
        max_call_duration: maxCallDuration
          ? Math.round(maxCallDuration * 60)
          : null,
      };
    } else {
      formData = {
        name: agentName,
        phone_number: formattedPhone,
        conversation_type: conversationType,
        instructions,
        instructions_type: instructionType,
        language_id: languageId,
        first_message: {
          type: firstMessageType,
          message:
            firstMessageType === "assistant-speaks-first"
              ? firstMessageText
              : "",
        },
        llm_provider: llmProvider,
        llm_model: llmModel || PROVIDER_DEFAULTS.llm_model,
        llm_config: llmRest,
        tts_provider: ttsProvider,
        tts_config: ttsConfig,
        asr_provider: asrProvider,
        asr_config: asrConfig,
        turn_taking: turnTaking,
        max_call_duration: maxCallDuration
          ? Math.round(maxCallDuration * 60)
          : null,
        // Legacy flat columns — kept for dual-write window. The backend
        // `sync_channels_to_legacy_columns` callback will also mirror these
        // from the channel jsonb, so sending both is safe.
        integration_type: derivedIntegrationType,
        twilio_phone_number: twilioPhoneNumber || null,
        twilio_phone_number_sid: twilioPhoneNumberSid || null,
        caller_phone_number_id: callerPhoneNumberId ? parseInt(callerPhoneNumberId, 10) : null,
        web_call_url: webCallUrl || null,
        integration_config: {
          ...(["livekit", "daily"].includes(effectiveWebProvider) && webEnabled && {
            room_management: roomManagement,
            bot_runner_url: botRunnerUrl || null,
            livekit_agent_name: livekitAgentName || null,
          }),
        },
        text_config: textChannelPayload?.config || {},
        // New channel-based fields (authoritative going forward)
        agent_kind: agentKind,
        agent_type: agentType,
        phone_channel: phoneChannelPayload,
        web_channel: webChannelPayload,
        text_channel: textChannelPayload,
      };
    }

    try {
      if (isEditMode) {
        await aiTestingAgentAPI.updateAgent(
          id,
          { agent: formData },
          currentWorkspace.id,
        );
      } else {
        const createRes = await aiTestingAgentAPI.createAgent(
          { agent: formData },
          currentWorkspace.id,
        );
        posthog?.capture('agent_created', {
          agent_id: createRes?.data?.id,
          integration_type: formData.integration_type,
        });
      }
      showToast(
        `Agent ${isEditMode ? "updated" : "created"} successfully`,
        "success",
      );
      await refreshAgents();
      navigate("/ai-testing-agent");
    } catch (err) {
      // The API returns either { error: "..." } or a Rails errors hash
      // ({ phone_number: ["doesn't look like a valid phone number ..."] }).
      // Surface the specific field message so a bad phone number isn't hidden
      // behind a generic "Failed to save".
      const data = err.response?.data;
      let msg = data?.error;
      if (!msg && data && typeof data === "object") {
        const [field, errors] = Object.entries(data)[0] || [];
        if (Array.isArray(errors) && errors.length) {
          msg = `${field.replace(/_/g, " ")} ${errors[0]}`;
        }
      }
      setError(msg || "Failed to save agent");
      showToast(msg || "Failed to save agent", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Language name for display ──────────────────────────────────────────────
  const selectedLangLabel =
    languages.find((l) => l.language_key === language)?.name || null;

  if (loading && isEditMode && !agentName) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black" />
      </div>
    );
  }

  return (
    <>
      <style>{phoneInputStyles}</style>
      <div className="px-4 pb-16">
        <FormAlert message={error} onDismiss={() => setError(null)} />

        <PageHeader
          description={
            isEditMode
              ? "Update this test agent's configuration, instructions, and call settings."
              : "Set up a test agent to call and evaluate your AI agent."
          }
        />

        <form onSubmit={handleSubmit}>
          <Tabs tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* ── Instructions tab ──────────────────────────────────────────── */}
          {activeTab === "instructions" && (
            <div className="grid grid-cols-3 gap-6">
              {/* Left column */}
              <div className="space-y-5 border border-border rounded-xl p-4">
                <Field
                  label="Test Agent Name"
                  hint="A name to identify this test agent in your workspace"
                  required
                >
                  <input
                    type="text"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    className={INPUT_CLS}
                    placeholder="e.g. Support Bot Tester"
                    required
                  />
                </Field>

                {/* Agent type selector — what the agent-under-test is. Drives the
                    instruction behavior; the transport (LiveKit/Daily/WebSocket)
                    is chosen separately on the Channels tab. */}
                <Field
                  label="Agent Type"
                  hint="What kind of agent are you testing?"
                >
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "custom", label: "Custom", icon: "custom.svg" },
                      { v: "pipecat", label: "Pipecat", icon: "pipecat-icon.png" },
                      { v: "livekit", label: "LiveKit", icon: "livekit-icon.svg" },
                      { v: "vapi", label: "Vapi", icon: "vapi-icon.svg" },
                      { v: "retell", label: "Retell", icon: "retell-icon.svg" },
                    ].map(({ v, label, icon }) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => selectAgentType(v)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          agentType === v
                            ? "border-gray-900 bg-muted text-foreground"
                            : "bg-card text-muted-foreground border-border hover:border-input"
                        }`}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/${icon}`}
                          alt=""
                          className="h-4 w-4 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>

                {/* Conversation Type applies to self-managed agents only */}
                {instructionType !== "external-provider" && (
                  <Field
                    label="Conversation Type"
                    hint="What type of calls does your agent handle?"
                    required
                  >
                    <select
                      value={conversationType}
                      onChange={(e) => setConversationType(e.target.value)}
                      className={SELECT_CLS}
                    >
                      <option value="inbound">Inbound</option>
                      <option value="outbound">Outbound</option>
                      <option value="both">Inbound & Outbound</option>
                    </select>
                  </Field>
                )}

                {/* Phone + Language apply to every agent. For external sync mode
                    they're populated by "Sync instructions from vendor"; in paste
                    mode (and self-managed) you set them here. */}
                <Field
                  label="Agent Phone Number"
                  hint="Your agent's phone number — we'll call this number during test scenarios."
                  required
                >
                  <PhoneInput
                    country="us"
                    value={phoneNumber}
                    onChange={(val) => setPhoneNumber(val)}
                    inputProps={{
                      required: true,
                    }}
                    enableSearch
                    disableSearchIcon
                    searchPlaceholder="Search countries"
                  />
                </Field>

                <Field
                  label="Primary Language"
                  hint="What language does your agent speak? Our test bot will call in this language."
                  required={instructionType !== "external-provider"}
                >
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={SELECT_CLS}
                    required={instructionType !== "external-provider"}
                  >
                    <option value="">Select Language</option>
                    {languages.map((lang) => (
                      <option key={lang.language_key} value={lang.language_key}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </Field>

                {/* Auto-sync configuration (external providers) */}
                {instructionType === "external-provider" && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <span className="block text-sm font-semibold text-foreground mb-1">
                      Auto-sync
                    </span>

                    {/* Instructions — fixed 24h cadence */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-foreground">
                          Sync instructions
                        </span>
                        <span className="text-xs text-muted-foreground">
                          every 24 hours
                        </span>
                      </div>
                      <div
                        onClick={() => {
                          if (extInstructionSource === "manual") return;
                          setExtAutoSyncInstructions(!extAutoSyncInstructions);
                        }}
                        title={
                          extInstructionSource === "manual"
                            ? "Disabled while using pasted instructions"
                            : undefined
                        }
                        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${
                          extAutoSyncInstructions ? "bg-gray-900" : "bg-gray-300"
                        } ${
                          extInstructionSource === "manual"
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${
                            extAutoSyncInstructions
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Calls — explanation, sync-from floor, interval, manual sync */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-foreground">Fetch calls</span>
                        <span className="text-xs text-muted-foreground">
                          Import real calls from {extVendor === "vapi" ? "Vapi" : "Retell"}
                        </span>
                      </div>
                      <div
                        onClick={() => setExtAutoFetchCalls(!extAutoFetchCalls)}
                        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${
                          extAutoFetchCalls ? "bg-gray-900" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${
                            extAutoFetchCalls ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </div>
                    </div>

                    {extAutoFetchCalls && (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          We pull calls your {extVendor === "vapi" ? "Vapi" : "Retell"} agent
                          handled and run each one through observation.{" "}
                          <span className="font-medium text-foreground">
                            Each fetched call is analysed and costs credits per call.
                          </span>{" "}
                          Imported calls show up under{" "}
                          <a
                            href="/observability/conversations"
                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            Call observation
                          </a>
                          .
                        </p>

                        {/* Sync-from floor — only calls on/after this date are imported */}
                        <div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="shrink-0">sync calls from</span>
                            <input
                              type="date"
                              value={extFetchFromDate}
                              onChange={(e) => setExtFetchFromDate(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-input rounded-lg bg-background focus:ring-primary/20 focus:border-primary"
                            />
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Only calls created on or after this date are imported, so you
                            don't pull the agent's entire history. Defaults to today.
                          </p>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="shrink-0">every</span>
                          <input
                            type="number"
                            min="1"
                            value={extFetchInterval}
                            onChange={(e) => setExtFetchInterval(e.target.value)}
                            className="w-14 px-2 py-1 text-sm border border-input rounded-lg focus:ring-primary/20 focus:border-primary"
                          />
                          <select
                            value={extFetchUnit}
                            onChange={(e) => setExtFetchUnit(e.target.value)}
                            className="flex-1 px-2 py-1 text-sm border border-input rounded-lg bg-background focus:ring-primary/20 focus:border-primary"
                          >
                            <option value="minutes">minutes</option>
                            <option value="hours">hours</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="shrink-0">min call duration</span>
                          <input
                            type="number"
                            min="0"
                            value={extMinCallDuration}
                            onChange={(e) =>
                              setExtMinCallDuration(e.target.value)
                            }
                            className="w-14 px-2 py-1 text-sm border border-input rounded-lg focus:ring-primary/20 focus:border-primary"
                          />
                          <span className="shrink-0">sec</span>
                        </div>

                        {/* Manual one-off sync from the chosen date */}
                        <button
                          type="button"
                          disabled={!isEditMode || extFetchingCalls}
                          onClick={async () => {
                            if (!extAssistantId?.trim()) {
                              showToast(
                                `Enter the ${extVendor === "vapi" ? "Assistant" : "Agent"} ID first`,
                                "error",
                              );
                              return;
                            }
                            setExtFetchingCalls(true);
                            try {
                              const res = await integrationAPI.fetchCalls(
                                currentWorkspace.id,
                                id,
                                extFetchFromDate,
                              );
                              showToast(
                                `Imported ${res.imported ?? 0} call(s)${
                                  res.skipped ? `, skipped ${res.skipped}` : ""
                                }`,
                                "success",
                              );
                            } catch (err) {
                              showToast(
                                err?.response?.data?.error || "Failed to sync calls",
                                "error",
                              );
                            } finally {
                              setExtFetchingCalls(false);
                            }
                          }}
                          className="w-full inline-flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium rounded-lg border border-border bg-card text-foreground hover:border-input transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${extFetchingCalls ? "animate-spin" : ""}`}
                          />
                          {extFetchingCalls ? "Syncing calls…" : "Sync from vendor"}
                        </button>
                        {!isEditMode && (
                          <p className="text-[11px] text-muted-foreground">
                            Save the agent first to sync calls from the vendor.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right columns */}
              <div className="col-span-2 space-y-5 border border-border rounded-xl p-4">
                {instructionType === "external-provider" ? (
                  <>
                    {/* Provider config section */}
                    <div className="space-y-5">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          External Provider
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Connect your{" "}
                          {extVendor === "vapi" ? "Vapi" : "Retell"} account to
                          sync instructions, calls, and agent config
                        </p>
                      </div>

                      {/* Integration-connection gate — user can't enter an
                          assistant ID until the vendor is connected for this
                          workspace. Inline Connect flow so they don't have
                          to bounce out to Settings. */}
                      {extIntegrationLoading ? (
                        <div className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2 border border-border">
                          Checking {extVendor === "vapi" ? "Vapi" : "Retell"} connection…
                        </div>
                      ) : extIntegration?.status === "connected" ? (
                        <div className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                          <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                          <span>
                            {extVendor === "vapi" ? "Vapi" : "Retell"} connected to this workspace.{" "}
                            <WorkspaceLink
                              to="/settings/integrations"
                              className="underline hover:text-emerald-900 font-medium"
                            >
                              Manage
                            </WorkspaceLink>
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-2 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="text-xs text-amber-800 font-medium">
                            {extVendor === "vapi" ? "Vapi" : "Retell"} isn't connected yet
                          </div>
                          <p className="text-xs text-amber-700">
                            Paste your {extVendor === "vapi" ? "Vapi" : "Retell"} API key
                            below to connect this workspace — this is stored encrypted and
                            used for all agents on {extVendor === "vapi" ? "Vapi" : "Retell"}.
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={extConnectKey}
                              onChange={(e) => setExtConnectKey(e.target.value)}
                              className={INPUT_CLS}
                              placeholder={
                                extVendor === "vapi"
                                  ? "vapi_xxx..."
                                  : "retell_xxx..."
                              }
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              onClick={connectVendorIntegration}
                              disabled={extConnecting || !extConnectKey.trim()}
                              className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors whitespace-nowrap"
                            >
                              {extConnecting ? "Connecting…" : "Connect"}
                            </button>
                          </div>
                          <p className="text-xs text-amber-600">
                            Prefer the full integrations UI?{" "}
                            <WorkspaceLink
                              to="/settings/integrations"
                              className="underline hover:text-amber-900"
                            >
                              Open Settings → Integrations
                            </WorkspaceLink>
                          </p>
                        </div>
                      )}

                      {/* Assistant / Agent ID — disabled until the vendor is
                          connected so the user can't enter an ID we can't
                          validate against. */}
                      <Field
                        label={
                          extVendor === "vapi" ? "Assistant ID" : "Agent ID"
                        }
                        hint={
                          extIntegration?.status === "connected"
                            ? `The ID of your ${extVendor === "vapi" ? "assistant" : "agent"} in ${extVendor === "vapi" ? "Vapi" : "Retell"}`
                            : `Connect ${extVendor === "vapi" ? "Vapi" : "Retell"} above first`
                        }
                        required
                      >
                        <input
                          type="text"
                          value={extAssistantId}
                          onChange={(e) => setExtAssistantId(e.target.value)}
                          disabled={extIntegration?.status !== "connected"}
                          className={`${INPUT_CLS} disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed`}
                          placeholder={
                            extVendor === "vapi"
                              ? "asst_xxxx..."
                              : "agent_xxxx..."
                          }
                        />
                      </Field>

                      {/* Instruction source — two buttons shown once a provider
                          is selected. Sync pulls opening prompt + instructions
                          from the vendor; Paste lets you define them yourself
                          (like a self-managed agent) and disables instruction
                          auto-sync. */}
                      <Field
                        label="Instructions"
                        hint={
                          extInstructionSource === "manual"
                            ? "You define the opening prompt and instructions — we won't pull them from the vendor"
                            : "Pull the latest opening prompt and instructions from your vendor"
                        }
                      >
                        <div className="flex gap-3">
                          <button
                            type="button"
                            disabled={extSyncing}
                            onClick={async () => {
                              setExtInstructionSource("sync");
                              if (!extAssistantId?.trim()) {
                                showToast(
                                  `Enter the ${extVendor === "vapi" ? "Assistant" : "Agent"} ID first`,
                                  "error",
                                );
                                return;
                              }
                              setExtSyncing(true);
                              try {
                                // Edit mode syncs into the saved agent; create
                                // mode previews the vendor config (no agent yet).
                                const res = isEditMode
                                  ? await integrationAPI.syncInstructions(
                                      currentWorkspace.id,
                                      id,
                                    )
                                  : await integrationAPI.previewVendorConfig(
                                      currentWorkspace.id,
                                      extVendor,
                                      extAssistantId,
                                    );
                                if (res.instructions)
                                  setInstructions(res.instructions);
                                if (res.phone_number)
                                  setPhoneNumber(
                                    res.phone_number.replace(/^\+/, ""),
                                  );
                                if (res.first_message) {
                                  const synced =
                                    res.first_message.type ||
                                    "assistant-speaks-first";
                                  setFirstMessageType(synced);
                                  setSyncedFirstMessageType(synced);
                                  setFirstMessageText(
                                    res.first_message.message || "",
                                  );
                                }
                                if (res.language) {
                                  const target = res.language.toLowerCase();
                                  const base = target.split("-")[0];
                                  const byKey = (val) =>
                                    languages.find(
                                      (l) => l.language_key?.toLowerCase() === val,
                                    );
                                  // Priority order so we don't grab the wrong
                                  // locale: exact key (en-US) → exact name →
                                  // base key (en) → any en-* as a last resort.
                                  const matchedLang =
                                    byKey(target) ||
                                    languages.find(
                                      (l) => l.name?.toLowerCase() === target,
                                    ) ||
                                    byKey(base) ||
                                    languages.find(
                                      (l) =>
                                        l.language_key
                                          ?.toLowerCase()
                                          .split("-")[0] === base,
                                    );
                                  if (matchedLang)
                                    setLanguage(matchedLang.language_key);
                                }
                                showToast(
                                  "Agent config synced from provider",
                                  "success",
                                );
                                // Rehydrate AgentContext so downstream pages
                                // (Scenario run, Batch) see the newly-synced
                                // phone_number without a browser reload.
                                refreshAgents();
                              } catch (err) {
                                showToast(
                                  err?.response?.data?.error ||
                                    "Failed to sync",
                                  "error",
                                );
                              } finally {
                                setExtSyncing(false);
                              }
                            }}
                            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                              extInstructionSource !== "manual"
                                ? "bg-gray-900 text-white border-gray-900"
                                : "bg-card text-muted-foreground border-border hover:border-input"
                            }`}
                          >
                            <RefreshCw
                              className={`h-4 w-4 ${extSyncing ? "animate-spin" : ""}`}
                            />
                            {extSyncing
                              ? "Syncing..."
                              : "Sync instructions from vendor"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setExtInstructionSource("manual");
                              setExtAutoSyncInstructions(false);
                            }}
                            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium rounded-lg border transition-colors ${
                              extInstructionSource === "manual"
                                ? "bg-gray-900 text-white border-gray-900"
                                : "bg-card text-muted-foreground border-border hover:border-input"
                            }`}
                          >
                            <ClipboardPaste className="h-4 w-4" />
                            Paste instructions
                          </button>
                        </div>
                      </Field>

                      {/* Paste mode — editable opening prompt + instructions,
                          mirroring the self-managed agent inputs. */}
                      {extInstructionSource === "manual" && (
                        <>
                          <OpeningPromptField
                            id="opening-prompt-pasted"
                            type={firstMessageType}
                            message={firstMessageText}
                            onTypeChange={setFirstMessageType}
                            onMessageChange={setFirstMessageText}
                          />

                          <Field
                            label="Agent Instructions"
                            hint="Your agent's system prompt / instructions. We use this to generate test scenarios and suggest prompt improvements."
                          >
                            <textarea
                              value={instructions}
                              onChange={(e) => setInstructions(e.target.value)}
                              className="w-full h-[15.6rem] px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black"
                              placeholder="Paste your agent's instructions here…"
                            />
                          </Field>
                        </>
                      )}
                    </div>

                    {/* Synced config preview (sync mode only — paste mode shows
                        editable inputs above instead of this read-only view) */}
                    {extInstructionSource !== "manual" &&
                      (instructions || syncedFirstMessageType) && (
                      <div className="space-y-4 border-t border-border pt-4 mt-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Synced from provider</p>

                        {instructions && extInstructionSource !== "manual" && (
                          <Field
                            label="Agent Instructions"
                            hint="Used to generate test scenarios and suggest prompt improvements"
                          >
                            <div className="bg-muted border border-border rounded-lg p-3 max-h-[15.6rem] overflow-y-auto">
                              <pre className="text-xs text-foreground whitespace-pre-wrap">{instructions}</pre>
                            </div>
                          </Field>
                        )}

                        {/* Editable, not a read-only echo: the synced value only
                            says who opens the call — the tester still chooses HOW
                            our bot opens it. */}
                        <OpeningPromptField
                          id="opening-prompt-synced"
                          type={firstMessageType}
                          message={firstMessageText}
                          onTypeChange={setFirstMessageType}
                          onMessageChange={setFirstMessageText}
                          hint="How should our test agent start the conversation? Pre-selected from your provider config."
                          bothSidesSilent={
                            syncedFirstMessageType !== "" &&
                            syncedFirstMessageType !== "assistant-waits-for-user"
                          }
                        />

                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <OpeningPromptField
                      id="opening-prompt"
                      type={firstMessageType}
                      message={firstMessageText}
                      onTypeChange={setFirstMessageType}
                      onMessageChange={setFirstMessageText}
                      required
                    />

                    <Field
                      label="Agent Instructions"
                      hint="Your agent's system prompt / instructions. We use this to generate test scenarios and suggest prompt improvements."
                    >
                      <textarea
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        className={`w-full h-[15.6rem] px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black`}
                        placeholder="Paste your agent's system prompt or instructions here..."
                        required
                      />
                    </Field>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Model Settings tab ────────────────────────────────────────── */}
          {activeTab === "model" && (
            <div className="space-y-5">
              {/* LLM */}
              <ProviderCard
                title="Language Model (LLM)"
                subtitle="Powers the conversational intelligence of the test caller"
                providers={LLM_PROVIDERS}
                selectedProvider={llmProvider}
                config={llmConfig}
                langCode={langCode}
                apiKeyHint={LLM_API_KEY_HINTS[llmProvider]}
                apiKeyConfigured={isKeyConfigured(llmProvider)}
                onProviderChange={handleLlmProviderChange}
                onConfigChange={(k, v) =>
                  setLlmConfig((prev) => ({ ...prev, [k]: v }))
                }
              />

              {/* Gemini 2.5 thinking budget — only relevant for Gemini 2.5 series */}
              {llmProvider === "gemini" &&
                String(llmConfig.model || "").startsWith("gemini-2.5") && (
                  <div className="border border-border rounded-xl p-4 space-y-3">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Thinking budget
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Token budget the model can spend on internal reasoning.
                        0 = no thinking (fastest time-to-first-token, recommended
                        for voice). Higher values trade latency for quality.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        className="text-sm border border-input rounded-lg px-3 py-2 focus:ring-primary/20 focus:border-primary"
                        value={
                          [0, 1024, 4096, 8192].includes(
                            Number(llmConfig.thinking_budget)
                          )
                            ? String(llmConfig.thinking_budget ?? 0)
                            : "custom"
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "custom") return;
                          setLlmConfig((prev) => ({
                            ...prev,
                            thinking_budget: Number(v),
                          }));
                        }}
                      >
                        <option value="0">0 (off — fastest)</option>
                        <option value="1024">1024</option>
                        <option value="4096">4096</option>
                        <option value="8192">8192</option>
                        <option value="custom">Custom</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="256"
                        className="text-sm border border-input rounded-lg px-3 py-2 w-32 focus:ring-primary/20 focus:border-primary"
                        value={llmConfig.thinking_budget ?? 0}
                        onChange={(e) =>
                          setLlmConfig((prev) => ({
                            ...prev,
                            thinking_budget: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                  </div>
                )}

              {/* TTS */}
              <ProviderCard
                title="Text-to-Speech (TTS)"
                subtitle="Converts the LLM's text responses into audio for the phone call"
                providers={TTS_PROVIDERS}
                selectedProvider={ttsProvider}
                config={ttsConfig}
                langCode={langCode}
                apiKeyHint={TTS_API_KEY_HINTS[ttsProvider]}
                apiKeyConfigured={isKeyConfigured(ttsProvider)}
                onProviderChange={handleTtsProviderChange}
                onConfigChange={(k, v) =>
                  setTtsConfig((prev) => ({ ...prev, [k]: v }))
                }
                voiceCatalogs={voiceCatalogs}
                onLoadVoiceCatalog={loadVoiceCatalog}
              />

              {/* ASR */}
              <ProviderCard
                title="Automatic Speech Recognition (ASR)"
                subtitle="Transcribes the agent's audio responses so the LLM can understand them"
                providers={ASR_PROVIDERS}
                selectedProvider={asrProvider}
                config={asrConfig}
                langCode={langCode}
                apiKeyHint={ASR_API_KEY_HINTS[asrProvider]}
                apiKeyConfigured={isKeyConfigured(asrProvider)}
                onProviderChange={handleAsrProviderChange}
                onConfigChange={(k, v) =>
                  setAsrConfig((prev) => ({ ...prev, [k]: v }))
                }
              />

              {/* Deepgram Nova endpointing — ms of silence before finalizing a turn.
                  Nova-only; Flux does its own turn detection (see eot_threshold below). */}
              {asrProvider === "deepgram" && asrConfig.model !== "flux-general-en" && (
                <div className="border border-border rounded-xl p-4 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Endpointing ({asrConfig.endpointing ?? 100} ms)
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Silence Deepgram waits before finalizing the transcript.
                      Lower = faster turn-taking, higher = fewer premature
                      cutoffs mid-sentence. 200 ms is a good default for voice.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="50"
                      max="500"
                      step="10"
                      value={asrConfig.endpointing ?? 100}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          endpointing: Number(e.target.value),
                        }))
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="50"
                      max="500"
                      step="10"
                      className="text-sm border border-input rounded-lg px-3 py-2 w-24 focus:ring-primary/20 focus:border-primary"
                      value={asrConfig.endpointing ?? 100}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          endpointing: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Deepgram Nova utterance-end — silence gap that closes an utterance.
                  Nova-only; Deepgram requires >= 1000 ms. Lower = snappier finalize. */}
              {asrProvider === "deepgram" && asrConfig.model !== "flux-general-en" && (
                <div className="border border-border rounded-xl p-4 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Utterance End ({asrConfig.utterance_end_ms ?? 1000} ms)
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Silence gap Deepgram waits before declaring the utterance
                      over. Lower = snappier turn-taking; higher tolerates longer
                      mid-sentence pauses. Deepgram minimum is 1000 ms.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="1000"
                      max="2000"
                      step="100"
                      value={asrConfig.utterance_end_ms ?? 1000}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          utterance_end_ms: Number(e.target.value),
                        }))
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="1000"
                      max="2000"
                      step="100"
                      className="text-sm border border-input rounded-lg px-3 py-2 w-24 focus:ring-primary/20 focus:border-primary"
                      value={asrConfig.utterance_end_ms ?? 1000}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          utterance_end_ms: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Deepgram Flux — end-of-turn threshold (Flux's own turn detection) */}
              {asrProvider === "deepgram" && asrConfig.model === "flux-general-en" && (
                <div className="border border-border rounded-xl p-4 space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      End-of-turn threshold ({(asrConfig.eot_threshold ?? 0.9).toFixed(2)})
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      How confident Flux must be that the caller has finished before the
                      agent replies. Higher = more patient (fewer cut-offs mid-sentence);
                      lower = snappier but may interrupt. Flux does its own turn detection,
                      so this replaces the silence gap. 0.90 is a safe default.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0.5"
                      max="0.9"
                      step="0.05"
                      value={asrConfig.eot_threshold ?? 0.9}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          eot_threshold: Number(e.target.value),
                        }))
                      }
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="0.5"
                      max="0.9"
                      step="0.05"
                      className="text-sm border border-input rounded-lg px-3 py-2 w-24 focus:ring-primary/20 focus:border-primary"
                      value={asrConfig.eot_threshold ?? 0.9}
                      onChange={(e) =>
                        setAsrConfig((prev) => ({
                          ...prev,
                          eot_threshold: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Turn-taking — per-agent override of the workspace/admin default */}
              <div className="border border-border rounded-xl p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Turn-taking
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      How the agent decides the caller has finished speaking. Off
                      = inherit the workspace default; on = override it for this
                      agent only. End-of-turn silence &amp; mode apply to the
                      Nova/VAD path (Deepgram Flux does its own turn detection).
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={turnTaking !== null}
                      onChange={(e) =>
                        setTurnTaking(
                          e.target.checked
                            ? {
                                smart_endpointing: "context",
                                end_of_turn_silence_secs: 0.8,
                                stop_timeout_secs: 0.3,
                                interruption_min_words: 0,
                                interruption_min_voice_secs: 0.2,
                              }
                            : null,
                        )
                      }
                    />
                    <div className="relative w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                  </label>
                </div>

                {turnTaking !== null && (
                  <div className="space-y-4 pt-1">
                    {/* Mode: Context-aware / Unified / Off */}
                    <div>
                      <label className="text-xs font-medium text-foreground">
                        Mode
                      </label>
                      <div className="flex gap-2 mt-1">
                        {[
                          { value: "context", label: "Context-aware" },
                          { value: "unified", label: "Unified" },
                          { value: "off", label: "Off" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setTurnTaking((prev) => ({
                                ...prev,
                                smart_endpointing: opt.value,
                              }))
                            }
                            className={`px-3 py-1.5 text-xs rounded-lg border ${
                              turnTaking.smart_endpointing === opt.value
                                ? "border-primary bg-primary/10 text-primary font-medium"
                                : "border-input text-muted-foreground"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {turnTaking.smart_endpointing === "context" && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          At the silence gap, a text model (words) and audio model (tone)
                          both vote — the turn ends only if both agree the caller is done,
                          and holds if either says mid-thought. Keep the gap large enough to
                          ride out between-sentence pauses. English only — else falls back
                          to an audio-only mode.
                        </p>
                      )}
                      {turnTaking.smart_endpointing === "unified" && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Audio only (Smart Turn + VAD, no text model). Holds patiently
                          when Smart Turn says the caller is mid-thought, briefly when
                          there's no verdict yet. Ceilings inherit the workspace default.
                        </p>
                      )}
                    </div>

                    {/* End-of-turn silence (VAD gate) */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-foreground">
                          End-of-turn silence
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {(turnTaking.end_of_turn_silence_secs ?? 0.8).toFixed(1)} s
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0.3"
                        max="3"
                        step="0.1"
                        value={turnTaking.end_of_turn_silence_secs ?? 0.8}
                        onChange={(e) =>
                          setTurnTaking((prev) => ({
                            ...prev,
                            end_of_turn_silence_secs: Number(e.target.value),
                          }))
                        }
                        className="w-full mt-1"
                      />
                    </div>

                    {/* Stop timeout — fallback grace after the VAD gate */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-foreground">
                          Stop timeout (grace)
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {(turnTaking.stop_timeout_secs ?? 0.3).toFixed(2)} s
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.05"
                        value={turnTaking.stop_timeout_secs ?? 0.3}
                        onChange={(e) =>
                          setTurnTaking((prev) => ({
                            ...prev,
                            stop_timeout_secs: Number(e.target.value),
                          }))
                        }
                        className="w-full mt-1"
                      />
                    </div>

                    {/* Interruption — min words */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-foreground">
                          Interruption — min words
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {turnTaking.interruption_min_words ?? 0}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        step="1"
                        value={turnTaking.interruption_min_words ?? 0}
                        onChange={(e) =>
                          setTurnTaking((prev) => ({
                            ...prev,
                            interruption_min_words: Number(e.target.value),
                          }))
                        }
                        className="w-full mt-1"
                      />
                    </div>

                    {/* Interruption — min voice seconds */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-foreground">
                          Interruption — min voice
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {(turnTaking.interruption_min_voice_secs ?? 0.2).toFixed(2)} s
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={turnTaking.interruption_min_voice_secs ?? 0.2}
                        onChange={(e) =>
                          setTurnTaking((prev) => ({
                            ...prev,
                            interruption_min_voice_secs: Number(e.target.value),
                          }))
                        }
                        className="w-full mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Max Call Duration */}
              <div className="border border-border rounded-xl p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Max Call Duration
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Call is automatically terminated after this time. Global
                    default is 10 minutes.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 flex-1 max-w-xs">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      step="1"
                      value={maxCallDuration ?? ""}
                      onChange={(e) =>
                        setMaxCallDuration(
                          e.target.value === "" ? null : Number(e.target.value),
                        )
                      }
                      placeholder="10"
                      className="w-24 px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-center"
                    />
                    <span className="text-sm text-muted-foreground">minutes</span>
                  </div>
                  {maxCallDuration !== null && (
                    <button
                      type="button"
                      onClick={() => setMaxCallDuration(null)}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      Reset to default (10 min)
                    </button>
                  )}
                </div>
                {maxCallDuration === null && (
                  <p className="text-xs text-muted-foreground">
                    Using global default: 10 minutes
                  </p>
                )}
                {maxCallDuration !== null && maxCallDuration > 0 && (
                  <p className="text-xs text-indigo-600">
                    Agent override: {maxCallDuration} minute
                    {maxCallDuration !== 1 ? "s" : ""} (
                    {Math.round(maxCallDuration * 60)}s)
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Channels tab (Phone / Web / Text) ─────────────────────────── */}
          {activeTab === "channels" && (
            <div className="space-y-5">
              <p className="text-xs text-muted-foreground px-1">
                Every agent gets a phone channel by default. Web and Text channels are optional. At test run time you'll pick which channel to exercise.
              </p>

              {/* Phone Channel — always enabled */}
              <div className="border border-border rounded-xl p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Phone Channel</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Dial the agent via a phone number over Twilio.</p>
                </div>

                <Field label="Provider">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "twilio", label: "Twilio", desc: "RubricHQ-managed" },
                      { v: "plivo", label: "Plivo", desc: "RubricHQ-managed" },
                      { v: "vobiz", label: "Vobiz", desc: "RubricHQ-managed" },
                      { v: "telnyx", label: "Telnyx", desc: "Beta" },
                      { v: "exotel", label: "Exotel", desc: "Beta" },
                      { v: "sip", label: "Generic SIP", desc: "Coming soon", disabled: true },
                    ].map(({ v, label, desc, disabled }) => (
                      <label
                        key={v}
                        title={disabled ? "Generic SIP is coming soon" : undefined}
                        className={`flex items-center gap-2 border rounded-lg px-3 py-2 transition-colors ${
                          phoneProvider === v ? "border-gray-900 bg-muted" : "border-border hover:border-input"
                        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name="phoneProvider"
                          value={v}
                          checked={phoneProvider === v}
                          disabled={disabled}
                          onChange={() => {
                            if (disabled) return;
                            setPhoneProvider(v);
                            setPhoneProviderExplicit(true);
                          }}
                          className="h-3.5 w-3.5"
                        />
                        <span>
                          <span className="block text-sm font-medium text-foreground leading-tight">{label}</span>
                          <span className="block text-xs text-muted-foreground">{desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </Field>
                {renderCallerIdPicker()}
              </div>

              {/* Web Channel */}
              <div className="border border-border rounded-xl p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Web Channel</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Connect via WebRTC or WebSocket for browser-based testing.</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <input type="checkbox" checked={webEnabled} onChange={(e) => setWebEnabled(e.target.checked)} className="h-4 w-4 rounded border-input" />
                    <span className="text-sm text-foreground">Enabled</span>
                  </label>
                </div>

                {webEnabled && (
                  <>
                    <Field label="Provider">
                      {agentKind === "internal" ? (
                        // Compact, content-width chips so the provider picker
                        // doesn't stretch the full page width — visually lighter
                        // (1px border, inline) than the Room Management cards below.
                        <div className="flex flex-wrap gap-2">
                          {[
                            { v: "livekit", label: "LiveKit", desc: "WebRTC rooms" },
                            { v: "daily", label: "Daily", desc: "WebRTC rooms" },
                            { v: "websocket", label: "WebSocket", desc: "Direct wss://" },
                          ].map(({ v, label, desc }) => (
                            <label
                              key={v}
                              className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                                webProvider === v ? "border-gray-900 bg-muted" : "border-border hover:border-input"
                              }`}
                            >
                              <input
                                type="radio"
                                name="webProvider"
                                value={v}
                                checked={webProvider === v}
                                onChange={() => setWebProvider(v)}
                                className="h-3.5 w-3.5"
                              />
                              <span>
                                <span className="block text-sm font-medium text-foreground leading-tight">{label}</span>
                                <span className="block text-xs text-muted-foreground">{desc}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className={`${SELECT_CLS} bg-muted text-foreground`}>
                          {agentKind === "external_vapi" ? "Vapi Web SDK (WebSocket)" : "Retell Web SDK (WebSocket)"}
                        </div>
                      )}
                    </Field>

                    {/* LiveKit / Daily config */}
                    {agentKind === "internal" && (webProvider === "livekit" || webProvider === "daily") && (
                      <>
                        <Field label="Room Management" hint="Who creates the room for each test call?">
                          <div className="grid grid-cols-2 gap-2 max-w-2xl">
                            {[
                              { v: "rubric", label: "We create rooms", desc: `We create rooms using your ${webProvider === "livekit" ? "LiveKit" : "Daily"} credentials` },
                              { v: "client", label: "You create rooms", desc: "We call your bot runner endpoint with our agent ID + metadata, you return room credentials" },
                            ].map(({ v, label, desc }) => (
                              <div key={v} onClick={() => setRoomManagement(v)}
                                className={`border-2 rounded-lg p-2.5 cursor-pointer transition-colors ${roomManagement === v ? "border-gray-900 bg-muted" : "border-border hover:border-input"}`}>
                                <p className="text-sm font-medium text-foreground">{label}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                              </div>
                            ))}
                          </div>
                        </Field>

                        {webProvider === "livekit" && roomManagement === "rubric" && (
                          <Field label="Agent Name" hint="Optional — for explicit LiveKit agent dispatch. Leave empty if your agent auto-dispatches.">
                            <input type="text" value={livekitAgentName} onChange={(e) => setLivekitAgentName(e.target.value)} className={INPUT_CLS} placeholder="e.g. my-voice-agent" />
                          </Field>
                        )}

                        <Field label="Bot Runner URL" hint={roomManagement === "rubric"
                          ? "Optional — we'll POST room credentials here so your bot knows to join"
                          : "Required — we POST to this URL, you return {room_url, token}"}>
                          <input type="text" value={botRunnerUrl} onChange={(e) => setBotRunnerUrl(e.target.value)} className={INPUT_CLS}
                            placeholder="https://your-server.com/api/session"
                            required={roomManagement === "client"} />
                        </Field>

                        {roomManagement === "client" && (
                          <div className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2.5 border border-border space-y-1.5">
                            <p className="font-medium text-foreground">What we POST to your bot runner</p>
                            <p>
                              On each test call we send a <code className="bg-card px-1 rounded border border-border">POST</code> with our agent's ID and metadata so you can create the right room:
                            </p>
                            <pre className="text-[11px] leading-relaxed text-foreground whitespace-pre-wrap">{`{
  "rubric_run_id": "<run id>",
  "agent_id": <your agent id>,
  "agent_metadata": {
    "id": <agent id>, "name": "...",
    "agent_type": "...", "integration_type": "...",
    "vendor_metadata": { ... }
  }
}`}</pre>
                            <p>Respond with <code className="bg-card px-1 rounded border border-border">{"{ room_url, token }"}</code> for our test bot to join.</p>
                          </div>
                        )}

                        {roomManagement === "rubric" && (
                          <>
                            <Field
                              label={`${webProvider === "livekit" ? "LiveKit" : "Daily"} credentials`}
                              hint="Optional — overrides the workspace credentials from Settings → Integrations for this agent only. Leave blank to use the workspace defaults."
                            >
                              {webProvider === "livekit" ? (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">LiveKit URL</label>
                                    <input type="text" value={livekitUrl} onChange={(e) => setLivekitUrl(e.target.value)} className={INPUT_CLS} placeholder="wss://your-project.livekit.cloud" autoComplete="off" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">API Key</label>
                                    <input type="password" value={livekitApiKey} onChange={(e) => setLivekitApiKey(e.target.value)} className={INPUT_CLS} placeholder="API Key" autoComplete="off" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">API Secret</label>
                                    {livekitSecretSet && !livekitSecretEditing ? (
                                      <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-3 py-2">
                                        <span className="text-sm text-muted-foreground">Configuration saved</span>
                                        <button
                                          type="button"
                                          onClick={() => { setLivekitSecretEditing(true); setLivekitApiSecret(""); }}
                                          className="text-xs font-medium text-primary hover:underline"
                                        >
                                          Edit
                                        </button>
                                      </div>
                                    ) : (
                                      <input type="password" value={livekitApiSecret} onChange={(e) => setLivekitApiSecret(e.target.value)} className={INPUT_CLS} placeholder="API Secret" autoComplete="off" />
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-xs font-medium text-muted-foreground mb-1">Daily API Key</label>
                                  {dailyKeySet && !dailyKeyEditing ? (
                                    <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-3 py-2">
                                      <span className="text-sm text-muted-foreground">Configuration saved</span>
                                      <button
                                        type="button"
                                        onClick={() => { setDailyKeyEditing(true); setDailyApiKey(""); }}
                                        className="text-xs font-medium text-primary hover:underline"
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  ) : (
                                    <input type="password" value={dailyApiKey} onChange={(e) => setDailyApiKey(e.target.value)} className={INPUT_CLS} placeholder="Daily API Key" autoComplete="off" />
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field
                              label={webProvider === "livekit" ? "LiveKit Config (JSON)" : "Room Properties (JSON)"}
                              hint={webProvider === "livekit"
                                ? "Optional — extra LiveKit room/dispatch options as JSON."
                                : "Optional — Daily room properties as JSON."}
                            >
                              <textarea
                                value={webProvider === "livekit" ? livekitConfigJson : roomPropertiesJson}
                                onChange={(e) =>
                                  webProvider === "livekit"
                                    ? setLivekitConfigJson(e.target.value)
                                    : setRoomPropertiesJson(e.target.value)
                                }
                                className="w-full h-24 px-3 py-2 border border-input rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-black"
                                placeholder={'{ "key": "value" }'}
                              />
                              {(() => {
                                const raw = webProvider === "livekit" ? livekitConfigJson : roomPropertiesJson;
                                if (!raw) return null;
                                try { JSON.parse(raw); return null; } catch {
                                  return <p className="text-xs text-red-500 mt-1">Invalid JSON</p>;
                                }
                              })()}
                            </Field>
                          </>
                        )}
                      </>
                    )}

                    {/* Internal WebSocket config */}
                    {agentKind === "internal" && webProvider === "websocket" && (
                      <Field label="WebSocket URL" hint="The wss:// endpoint where your agent listens for connections" required>
                        <input type="text" value={webCallUrl} onChange={(e) => setWebCallUrl(e.target.value)} className={INPUT_CLS}
                          placeholder="wss://your-agent.example.com/ws" required />
                      </Field>
                    )}

                    {/* External vendor web — no extra config; uses integration API keys */}
                    {agentKind !== "internal" && (
                      <div className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2 border border-border">
                        Web calls are placed via the vendor's SDK using the API key configured in{" "}
                        <WorkspaceLink to="/settings/integrations" className="text-indigo-600 hover:text-indigo-800 font-medium">Settings → Integrations</WorkspaceLink>.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Text Channel — hidden for external vendor agents */}
              {agentKind === "internal" && (
                <div className="border border-border rounded-xl p-4 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Text Channel</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Test the agent with text messages over a WebSocket connection.</p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer shrink-0">
                      <input type="checkbox" checked={textEnabled} onChange={(e) => setTextEnabled(e.target.checked)} className="h-4 w-4 rounded border-input" />
                      <span className="text-sm text-foreground">Enabled</span>
                    </label>
                  </div>

                  {textEnabled && (
                    <>
                      {/* Text testing uses a single format: the X-RUBRIC WebSocket
                          protocol. See the API docs for a runnable example. */}
                          <Field label="WebSocket URL" hint="e.g. wss://your-agent.com/ws">
                            <input type="url" value={textWebsocketUrl} onChange={(e) => setTextWebsocketUrl(e.target.value)} className={INPUT_CLS} placeholder="wss://your-agent.com/ws" />
                          </Field>
                          <Field label="Secret" hint="Sent as X-RUBRIC-SECRET header">
                            <input type="password" value={textWsSecret} onChange={(e) => setTextWsSecret(e.target.value)} className={INPUT_CLS} placeholder={textWsSecretSet ? "•••••• (secret saved — leave blank to keep)" : "secret..."} autoComplete="off" />
                          </Field>
                          <Field label="Per-turn timeout (seconds)" hint="Max time to wait for the agent's reply to each message">
                            <input type="number" min={5} value={textPerTurnTimeout} onChange={(e) => setTextPerTurnTimeout(Number(e.target.value))} className={INPUT_CLS} />
                          </Field>
                          <Field label="Max session duration (seconds)" hint="Hard limit on a single test session">
                            <input type="number" min={30} value={textMaxSessionDuration} onChange={(e) => setTextMaxSessionDuration(Number(e.target.value))} className={INPUT_CLS} />
                          </Field>
                          <p className="text-xs text-muted-foreground">
                            We open a WebSocket to this <code className="bg-muted px-1 rounded">wss://</code> URL, send each user turn as <code className="bg-muted px-1 rounded">{'{"content":"..."}'}</code>, and expect agent replies as <code className="bg-muted px-1 rounded">{'{"content":"..."}'}</code>. The secret is sent as the <code className="bg-muted px-1 rounded">X-RUBRIC-SECRET</code> header.
                          </p>
                    </>
                  )}
                </div>
              )}

            </div>
          )}

          {/* ── (Legacy Call Transport UI — retained but unreachable) ─────── */}
          {false && activeTab === "telephony" && (
            <div className="space-y-5">
              {/* Transport type selector */}
              <div className="border border-border rounded-xl p-4 space-y-5">
                <Field label="Transport Type" hint="How should our test bot connect to your agent?">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { v: "pipecat_twilio", label: "Phone (Twilio)", desc: "Dial via phone number" },
                      { v: "livekit_managed", label: "LiveKit", desc: "WebRTC via LiveKit rooms" },
                      { v: "daily_managed", label: "Daily", desc: "WebRTC via Daily rooms" },
                      { v: "websocket", label: "WebSocket", desc: "Direct wss:// connection" },
                    ].map(({ v, label, desc }) => (
                      <div
                        key={v}
                        onClick={() => setTransportType(v)}
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                          transportType === v
                            ? "border-gray-900 bg-muted/50"
                            : "border-border hover:border-input"
                        }`}
                      >
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>

              {/* Phone (Twilio) config */}
              {transportType === "pipecat_twilio" && (
                <div className="border border-border rounded-xl p-4 space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Twilio Phone Call</h3>
                    <p className="text-sm text-muted-foreground mt-1">Our test bot dials your agent's phone number via Twilio.</p>
                  </div>
                  {renderCallerIdPicker()}
                </div>
              )}

              {/* LiveKit config */}
              {transportType === "livekit_managed" && (
                <div className="border border-border rounded-xl p-4 space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">LiveKit Configuration</h3>
                    <p className="text-sm text-muted-foreground mt-1">Our test bot joins a LiveKit room alongside your agent.</p>
                  </div>

                  <Field label="Room Management" hint="Who creates the LiveKit room for each test call?">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "rubric", label: "We create rooms", desc: "We create rooms on your LiveKit server using your API credentials" },
                        { v: "client", label: "You create rooms", desc: "We call your bot runner endpoint, you return room credentials" },
                      ].map(({ v, label, desc }) => (
                        <div key={v} onClick={() => setRoomManagement(v)}
                          className={`border-2 rounded-lg p-2.5 cursor-pointer transition-colors ${roomManagement === v ? "border-gray-900 bg-muted/50" : "border-border hover:border-input"}`}>
                          <p className="text-sm font-medium text-foreground">{label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                        </div>
                      ))}
                    </div>
                  </Field>

                  {roomManagement === "rubric" && (
                    <Field label="Agent Name" hint="Optional — for explicit LiveKit agent dispatch. Leave empty if your agent auto-dispatches.">
                      <input type="text" value={livekitAgentName} onChange={(e) => setLivekitAgentName(e.target.value)} className={INPUT_CLS} placeholder="e.g. my-voice-agent" />
                    </Field>
                  )}

                  <Field label="Bot Runner URL" hint={roomManagement === "rubric"
                    ? "Optional — we'll POST room credentials here so your bot knows to join"
                    : "Required — we POST to this URL, you return {room_url, token}"}>
                    <input type="text" value={botRunnerUrl} onChange={(e) => setBotRunnerUrl(e.target.value)} className={INPUT_CLS}
                      placeholder="https://your-server.com/api/session"
                      required={roomManagement === "client"} />
                  </Field>

                  <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                    <p className="font-medium mb-1">{roomManagement === "rubric" ? "How it works:" : "Bot Runner Contract:"}</p>
                    {roomManagement === "rubric" ? (
                      <p>We create a room on your LiveKit server{livekitAgentName ? `, dispatch agent "${livekitAgentName}"` : " (auto-dispatch)"}, then our test bot joins. {botRunnerUrl ? "We'll also notify your bot runner with room credentials." : ""}</p>
                    ) : (
                      <div>
                        <p>We POST to your endpoint:</p>
                        <pre className="bg-card border rounded p-2 mt-1 font-mono">{'{"rubric_run_id": "...", "metadata": {...}}'}</pre>
                        <p className="mt-1">You return:</p>
                        <pre className="bg-card border rounded p-2 mt-1 font-mono">{'{"room_url": "wss://...", "token": "eyJ..."}'}</pre>
                      </div>
                    )}
                  </div>

                  {roomManagement === "rubric" && (
                    <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border">
                      LiveKit credentials are managed in{" "}
                      <WorkspaceLink to="/settings/integrations" className="text-indigo-600 hover:text-indigo-800 font-medium">Settings → Integrations</WorkspaceLink>.
                      You can override per-agent in the integration config.
                    </div>
                  )}
                </div>
              )}

              {/* Daily config */}
              {transportType === "daily_managed" && (
                <div className="border border-border rounded-xl p-4 space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Daily Configuration</h3>
                    <p className="text-sm text-muted-foreground mt-1">Our test bot joins a Daily room alongside your agent.</p>
                  </div>

                  <Field label="Room Management" hint="Who creates the Daily room for each test call?">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "rubric", label: "We create rooms", desc: "We create rooms using your Daily API key" },
                        { v: "client", label: "You create rooms", desc: "We call your bot runner endpoint, you return room credentials" },
                      ].map(({ v, label, desc }) => (
                        <div key={v} onClick={() => setRoomManagement(v)}
                          className={`border-2 rounded-lg p-2.5 cursor-pointer transition-colors ${roomManagement === v ? "border-gray-900 bg-muted/50" : "border-border hover:border-input"}`}>
                          <p className="text-sm font-medium text-foreground">{label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                        </div>
                      ))}
                    </div>
                  </Field>

                  <Field label="Bot Runner URL" hint={roomManagement === "rubric"
                    ? "Required — we'll POST room credentials here so your bot knows to join"
                    : "Required — we POST to this URL, you return {room_url, token}"}>
                    <input type="text" value={botRunnerUrl} onChange={(e) => setBotRunnerUrl(e.target.value)} className={INPUT_CLS}
                      placeholder="https://your-server.com/api/session" required />
                  </Field>

                  <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                    <p className="font-medium mb-1">{roomManagement === "rubric" ? "How it works:" : "Bot Runner Contract:"}</p>
                    {roomManagement === "rubric" ? (
                      <p>We create a Daily room using your API key, generate meeting tokens, and POST the room credentials to your bot runner so your agent joins. Then our test bot joins the same room.</p>
                    ) : (
                      <div>
                        <p>We POST to your endpoint:</p>
                        <pre className="bg-card border rounded p-2 mt-1 font-mono">{'{"rubric_run_id": "...", "metadata": {...}}'}</pre>
                        <p className="mt-1">You return:</p>
                        <pre className="bg-card border rounded p-2 mt-1 font-mono">{'{"room_url": "https://your-domain.daily.co/room-name", "token": "eyJ..."}'}</pre>
                      </div>
                    )}
                  </div>

                  {roomManagement === "rubric" && (
                    <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border">
                      Daily API key is managed in{" "}
                      <WorkspaceLink to="/settings/integrations" className="text-indigo-600 hover:text-indigo-800 font-medium">Settings → Integrations</WorkspaceLink>.
                    </div>
                  )}
                </div>
              )}

              {/* WebSocket config */}
              {transportType === "websocket" && (
                <div className="border border-border rounded-xl p-4 space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">WebSocket Configuration</h3>
                    <p className="text-sm text-muted-foreground mt-1">Our test bot connects directly to your agent's WebSocket endpoint.</p>
                  </div>

                  <Field label="WebSocket URL" hint="The wss:// endpoint where your agent listens for connections" required>
                    <input type="text" value={webCallUrl} onChange={(e) => setWebCallUrl(e.target.value)} className={INPUT_CLS}
                      placeholder="wss://your-agent.example.com/ws" required />
                  </Field>

                  <div className="bg-muted/50 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                    <p className="font-medium mb-1">How it works:</p>
                    <p>Our test bot connects to your WebSocket endpoint and exchanges audio/text messages directly. No room abstraction — a direct two-party connection.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Text Testing tab ──────────────────────────────────────── */}
          {activeTab === "text_testing" && (
            <div className="space-y-5">
              <div className="border border-border rounded-xl p-4 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Text Testing Configuration
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure how this agent exposes a text interface for
                    text-based scenario testing. Leave blank if this agent is
                    voice-only.
                  </p>
                </div>

                {/* Endpoint type toggle */}
                <Field label="Interface Type">
                  <div className="flex gap-2 mt-1">
                    {[
                      { value: "rest", label: "REST API" },
                      {
                        value: "custom_websocket",
                        label: "Custom WebSocket",
                      },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setTextEndpointType(opt.value)}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          textEndpointType === opt.value
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-card text-muted-foreground border-input hover:border-gray-400"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Field>

                {textEndpointType === "rest" && (
                  <>
                    <Field
                      label="API Endpoint"
                      hint="e.g. https://your-agent.com/v1/chat/completions"
                    >
                      <input
                        type="url"
                        value={textEndpoint}
                        onChange={(e) => setTextEndpoint(e.target.value)}
                        className={INPUT_CLS}
                        placeholder="https://..."
                      />
                    </Field>
                    <Field
                      label="API Key"
                      hint="Sent as Authorization: Bearer <key>"
                    >
                      <input
                        type="password"
                        value={textApiKey}
                        onChange={(e) => setTextApiKey(e.target.value)}
                        className={INPUT_CLS}
                        placeholder="Bearer token for your endpoint (optional)"
                        autoComplete="off"
                      />
                    </Field>
                    <Field label="API Format">
                      <select
                        value={textApiFormat}
                        onChange={(e) => setTextApiFormat(e.target.value)}
                        className={SELECT_CLS}
                      >
                        <option value="openai_chat">
                          OpenAI Chat Completions
                        </option>
                        <option value="anthropic">Anthropic Messages</option>
                        <option value="custom">Custom (JSON)</option>
                      </select>
                    </Field>
                    <p className="text-xs text-muted-foreground">
                      We send <code className="bg-muted px-1 rounded">POST {"{endpoint}"}/chat/completions</code> with <code className="bg-muted px-1 rounded">{"{ messages: [...] }"}</code> and read <code className="bg-muted px-1 rounded">choices[0].message.content</code>. Request format follows the API Format selected above.
                    </p>
                  </>
                )}

                {textEndpointType === "custom_websocket" && (
                  <>
                    <Field
                      label="WebSocket URL"
                      hint="e.g. wss://your-agent.com/ws"
                    >
                      <input
                        type="url"
                        value={textWebsocketUrl}
                        onChange={(e) => setTextWebsocketUrl(e.target.value)}
                        className={INPUT_CLS}
                        placeholder="wss://your-agent.com/ws"
                      />
                    </Field>
                    <Field
                      label="Secret"
                      hint="Sent as X-RUBRIC-SECRET header"
                    >
                      <input
                        type="password"
                        value={textWsSecret}
                        onChange={(e) => setTextWsSecret(e.target.value)}
                        className={INPUT_CLS}
                        placeholder={
                          textWsSecretSet
                            ? "•••••• (secret saved — leave blank to keep)"
                            : "secret..."
                        }
                        autoComplete="off"
                      />
                    </Field>
                    <Field
                      label="Per-turn timeout (seconds)"
                      hint="Max time to wait for the agent's reply to each message"
                    >
                      <input
                        type="number"
                        min={5}
                        value={textPerTurnTimeout}
                        onChange={(e) =>
                          setTextPerTurnTimeout(Number(e.target.value))
                        }
                        className={INPUT_CLS}
                      />
                    </Field>
                    <Field
                      label="Max session duration (seconds)"
                      hint="Hard limit on a single test session"
                    >
                      <input
                        type="number"
                        min={30}
                        value={textMaxSessionDuration}
                        onChange={(e) =>
                          setTextMaxSessionDuration(Number(e.target.value))
                        }
                        className={INPUT_CLS}
                      />
                    </Field>
                    <p className="text-xs text-muted-foreground">
                      We open a WebSocket to this <code className="bg-muted px-1 rounded">wss://</code> URL, send each user turn as <code className="bg-muted px-1 rounded">{'{"content":"..."}'}</code>, and expect agent replies as <code className="bg-muted px-1 rounded">{'{"content":"..."}'}</code>. The secret is sent as the <code className="bg-muted px-1 rounded">X-RUBRIC-SECRET</code> header.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/ai-testing-agent")}
              className="px-4 py-2 border border-input rounded-lg text-sm text-foreground hover:bg-muted/50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !agentName ||
                !phoneNumber ||
                (instructionType !== "external-provider" &&
                  (!language || !instructions)) ||
                (instructionType === "external-provider" &&
                  (!extAssistantId || extIntegration?.status !== "connected"))
              }
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Create Agent"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateAgent;
